/**
 * <route-backdrop>
 *
 * A fixed, full-screen route that runs behind the entire page, drawn in step
 * with total document scroll rather than one section's transit.
 *
 * The reference sites take this further: they suppress native scrolling
 * altogether and drive a timeline from wheel input, which is why they feel like
 * one continuous film. That is not a trade worth making inside a Shopify theme —
 * it breaks keyboard scrolling, the theme editor, deep links and every assistive
 * technology. The effect that matters is a persistent backdrop the page travels
 * across, and native scroll gives that with none of the cost.
 *
 * Progress is scrollY / (scrollHeight - innerHeight), so the ribbon completes as
 * the reader reaches the foot of the page. Waypoints are placed at fractions of
 * that, which is how a stop lines up with the section that discusses it.
 *
 * The signs are HTML, not canvas. They are placed with the same CSS 3D matrix
 * three.js uses for CSS3DObject, which puts them in the scene's own perspective
 * rather than merely near it — so the board and its words are one object and
 * cannot drift apart. They stay selectable, translatable and readable by a
 * crawler, which is the whole reason not to draw text into the canvas.
 */

/*
 * The stretch of curve the reader travels, and where the stops sit on it.
 *
 * Scroll maps onto TRAVEL_FROM..TRAVEL_TO rather than the whole curve, which
 * leaves road ahead of the final stop for the camera to still be driving into.
 */
const TRAVEL_FROM = 0.02;
/*
 * Short of the curve's end on purpose. The aim reaches a good way further than
 * the camera, and run to the very end it looked straight at the terminus —
 * where the strands curl and stop.
 */
const TRAVEL_TO = 0.78;
/*
 * Where the stops sit on that stretch.
 *
 * Both are points on the curve, and they have to be read against the travelled
 * range above, not against the whole road: at 0.34 the first stop was a third
 * of the way along a journey that starts at 0.02, so two-fifths of the page
 * scrolled past with nothing on it, and the rest of the stops were crowded into
 * what was left. A short lead-in — about an eighth of the scroll, enough to
 * take in the opening line and the road — then evenly spaced the rest of the way.
 */
const FIRST_STOP = 0.15;
const LAST_STOP = 0.86;

/*
 * A sign is authored at SIGN_PX wide and scaled to SIGN_UNITS in the scene, so
 * the type inside it is designed once at a comfortable size and the perspective
 * does the rest. Changing the ratio changes how large the boards read.
 */
const SIGN_PX = 880;
const SIGN_UNITS = 9.4;

/*
 * CSS 3D works in pixels: a transform's world units *are* CSS pixels. This
 * scene is in metres, so handing it the camera directly put the lens sixteen
 * pixels from a sign and the projection collapsed. Everything in the CSS layer
 * is therefore scaled by this, which is exactly the ratio that makes a sign
 * authored at SIGN_PX come out SIGN_UNITS wide in the scene. Scaling positions
 * and camera together leaves the projection identical — only the units change.
 */
const PX_PER_UNIT = SIGN_PX / SIGN_UNITS;

/*
 * Fog starts well out. Close in it was a grey wash over everything — the whole
 * scene read as misty and the light bands lost their edge against it.
 */
const FOG_NEAR = 34;
/*
 * Far. It was pulled in to 125 to keep the city out of sight until the end, but
 * that is a blunt instrument: everything past it goes black, mountains and
 * skyline included. The climb hides the city now — you top a rise and it is
 * there — so the haze can go back to being haze.
 */
const FOG_FAR = 250;

/* The braid: how many filaments by default, and how wide the bundle runs. */
const STRANDS = 26;
const BRAID_W = 1.0;

/*
 * Where a stop stands: a little to one side of the road, and well above it.
 *
 * The offset is constant. An earlier version eased it inward on approach,
 * because words at a fixed offset grew until they overran the frame — but the
 * scale is capped at actual size now, so the widest they ever get is fixed and
 * one offset that fits at the closest point fits everywhere.
 */
const SIGN_OUT = 1.0;
const SIGN_UP = 3.1;

/*
 * Where a stop sits vertically in the frame, in normalised device coordinates:
 * zero is the middle, positive is above it. Held there rather than left to the
 * road's height, which moved every stop up and down the page as the route rose
 * and fell.
 *
 * Well above centre, because the road fills the bottom of the frame: at a tenth
 * the last line of a stop was landing on the brightest part of it.
 */
const SIGN_NDC_Y = 0.26;

/*
 * And how far off centre, as a fraction of half the frame — by the side the
 * stop stands on, so the page does not march down one edge.
 */
const SIGN_NDC_X = 0.3;

/* The corridor the filaments run along, and how far the land reaches. */
const VALLEY = 3.4;
/*
 * Wide. From the crest the reader can see a long way, and at 130 the mesh ran
 * out mid-frame — a hard straight edge with black beyond it.
 */
const LAND_W = 320;

const epsilon = (v) => (Math.abs(v) < 1e-10 ? 0 : v);

/* The two matrix serialisations three.js uses for CSS3D, unchanged. */
function cameraCSSMatrix(m) {
  const e = m.elements;
  return `matrix3d(${[
    e[0], -e[1], e[2], e[3],
    e[4], -e[5], e[6], e[7],
    e[8], -e[9], e[10], e[11],
    e[12], -e[13], e[14], e[15],
  ].map(epsilon).join(',')})`;
}

function objectCSSMatrix(m) {
  const e = m.elements;
  return `translate(-50%,-50%)matrix3d(${[
    e[0], e[1], e[2], e[3],
    -e[4], -e[5], -e[6], -e[7],
    e[8], e[9], e[10], e[11],
    e[12], e[13], e[14], e[15],
  ].map(epsilon).join(',')})`;
}

/*
 * A number out of something a person typed.
 *
 * These arrive from text fields, where "18", "18%" and " 18 % " all plainly
 * mean eighteen. Number() disagrees with two of those and answers NaN, which
 * reads as "not set" and silently falls back to the automatic behaviour — the
 * setting appears to do nothing at all, which is worse than an error.
 */
const asNumber = (raw) => {
  if (raw === undefined || raw === null || raw === '') return NaN;
  const n = Number(String(raw).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : NaN;
};

class RouteBackdrop extends HTMLElement {
  #raf = 0;
  #p = 0;
  #t0 = 0;
  #v;
  #rest = 0;
  #heading = 1;
  #lastY = 0;
  #session = null;
  #deltas = [];
  #wheelAt = 0;
  #lockUntil = 0;
  // Scene only, no journey. See the comment on #backdropProgress.
  #back = false;
  #stepping = false;
  #holding = false;
  #stepTo = null;
  #landed = null;
  #landedAt = 0;
  #intent = 0;
  #intentAt = 0;
  #touchY = 0;
  #touchFrom = 0;
  #pagerDone = false;
  #pagerFree = false;
  #pager = null;
  #dpr = 0;
  #slow = 0;
  #frame = 0;
  #beat = 0;
  #beats = [];
  #half = false;
  #skip = false;
  #up;
  #route;
  #a;
  #c;
  #r;
  #aimed = null;
  #sizes = new Map();
  #safe = null;
  #safeAge = 0;
  #until = 0;
  #gesture = 0;
  #peak = 0;
  #stepped = 0;
  #slide = 0;

  /*
   * Hand the stops back to the document.
   *
   * The section hides its list while this component decides whether there is
   * going to be a journey at all, so every way out of connectedCallback has to
   * come through here — otherwise a reader on reduced motion, or a browser
   * without WebGL, is left looking at an intro and nothing else.
   */
  #uncover() {
    const route = this.closest('.route');
    if (!route) return;
    clearTimeout(route.__routeBoot);
    route.classList.remove('route--booting');
    /*
     * There will be no scrubbing, so every frame sheet is now a still and has
     * to be fetched — nothing else is going to ask for it.
     */
    route.querySelectorAll('[data-film]').forEach((film) => {
      film.dataset.ready = 'true';
    });
  }

  async connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';
    this.#back = this.dataset.mode === 'backdrop';
    /*
     * A backdrop behind a page that has a journey of its own is hidden by CSS,
     * and must not build anything.
     *
     * Not loading the module is not enough to prevent this: the journey's own
     * section loads the same module, and defining a custom element upgrades
     * every matching element already in the document — including this one,
     * sitting in a display:none layer. Left to it, a journey page ends up with
     * two WebGL contexts, the second of which is never seen and is exactly the
     * kind of thing a browser starts refusing to hand out a third after.
     */
    if (this.#back) {
      const layer = this.closest('.page-backdrop');
      if (layer && getComputedStyle(layer).display === 'none') return;
    }
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return this.#uncover();
    if (!document.createElement('canvas').getContext('webgl2')) return this.#uncover();

    /*
     * The cover is the component's from here. Three.js is a large module on a
     * cold cache and the section's four seconds are shorter than that download,
     * so the net is lengthened rather than removed: whatever happens between
     * here and going live — a throw in setup, a renderer that never builds —
     * the stops come back.
     */
    const booting = this.closest('.route');
    if (booting) {
      clearTimeout(booting.__routeBoot);
      booting.__routeBoot = setTimeout(() => this.#uncover(), 20000);
    }

    let THREE;
    try { THREE = await import(this.dataset.three); }
    catch (e) {
      console.warn('route-backdrop: three.js unavailable', e);
      return this.#uncover();
    }

    this.T = THREE;
    this.#v = new THREE.Vector3();
    this.#up = new THREE.Vector3();
    this.#a = new THREE.Vector3();
    this.#c = new THREE.Vector3();
    this.#r = new THREE.Vector3();
    this.accent = new THREE.Color(this.dataset.color || '#E63950');
    this.dim = new THREE.Color(this.dataset.dim || '#333333');
    this.stops = Math.max(Number(this.dataset.stops) || 6, 2);
    this.grow = this.dataset.arrive !== 'fade';
    // How far across the frame the road is carried, as a share of its width.
    this.roadShift = Math.min(Math.max(Number(this.dataset.roadShift) || 0, 0), 0.4);
    // And how far down it, as a share of the frame's height.
    this.roadDrop = Math.min(Math.max(Number(this.dataset.roadDrop) || 0, 0), 0.4);
    /*
     * How many filaments in the bundle. A setting rather than a constant —
     * it is the single control over how heavy the braid reads, and the right
     * number depends on the colour as much as anything: the same count in
     * yellow carries far more light than in blue.
     */
    this.nStrands = Math.min(Math.max(Number(this.dataset.strands) || STRANDS, 6), 60);
    /*
     * Two more dials on the braid's look, both relative to the tuned defaults:
     * how far the strands sit from the centre line (and so how tightly they
     * gather), and how thick each filament's bright core is drawn.
     */
    const spread = Number(this.dataset.spread);
    this.spread = Number.isFinite(spread) && spread > 0 ? Math.min(Math.max(spread, 0.3), 2) : 1;
    // How fast the comets run, relative to the tuned default.
    const pace = Number(this.dataset.pulseSpeed);
    this.pulseSpeed = Number.isFinite(pace) && pace >= 0 ? Math.min(pace, 4) : 1;
    const thread = Number(this.dataset.thread);
    this.thread = Number.isFinite(thread) && thread > 0 ? Math.min(Math.max(thread, 0.5), 4) : 1;
    /*
     * Where a stop sits in the frame, as a fraction of it from the middle and
     * up from the centre line. Defaults that were constants, because the right
     * answer depends on how long the words are and how tall the screen is —
     * which is a thing to be looked at rather than derived.
     */
    this.signX = Number.isFinite(Number(this.dataset.cardX))
      ? Number(this.dataset.cardX) : SIGN_NDC_X;
    // Line stops up with the page edges (the default), or hold them a set
    // distance from the middle of the frame.
    this.alignToPage = this.dataset.align !== 'offset';
    this.signY = Number.isFinite(Number(this.dataset.cardY))
      ? Number(this.dataset.cardY) : SIGN_NDC_Y;
    /*
     * The rank stride has to be coprime with the count or the ladder folds:
     * step seven through twenty-eight strands and only four brightnesses ever
     * get used, on four strands each, and the bundle goes back to being a rope.
     */
    this.stride = 7;
    const hcf = (a, b) => (b ? hcf(b, a % b) : a);
    while (hcf(this.stride, this.nStrands) !== 1) this.stride += 2;
    this.fogColour = new THREE.Color(this.dataset.fog || '#100A0C');
    // The colour a filament's centre burns to.
    this.hot = this.accent.clone().lerp(new THREE.Color('#FFFFFF'), 0.32);

    /*
     * How bright this accent is, against the one the scene was balanced on.
     *
     * Everything that lights the world is the accent multiplied by something —
     * the spill baked into the land, the city, the motes, the braid. So the
     * brightness of the whole scene rides on which colour the merchant picks,
     * and yellow carries roughly three and a half times the luminance of the
     * red it was tuned against. Left alone, changing one colour field blows the
     * landscape out.
     *
     * three converts a hex to linear on the way in, so these components are
     * already light rather than encoded values, and the usual coefficients give
     * a real luminance. Dividing by it holds any accent at the brightness the
     * scene was built for; the clamp stops a very dark colour trying to
     * compensate its way to white.
     */
    const luma = (c) => 0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b;
    const reference = luma(new THREE.Color('#E63950'));
    /*
     * Normalised against the accent, then scaled by the merchant. The first
     * keeps yellow from arriving three times brighter than blue; the second is
     * the only control over how much light the whole thing carries, which no
     * amount of normalising can decide on anyone's behalf.
     */
    /*
     * Steady light and surge, each on its own control. Brightness above scales
     * the whole bundle; these decide how it is divided between the light a
     * strand always gives and the pulse that runs down it.
     */
    const hal = Number(this.dataset.lit);
    const sur = Number(this.dataset.surge);
    this.haloGain = Number.isFinite(hal) && hal > 0 ? hal : 0.3;
    this.surgeGain = Number.isFinite(sur) && sur >= 0 ? sur : 0.9;

    const gain = Number(this.dataset.glow);
    this.glow = Math.min(Math.max(reference / Math.max(luma(this.accent), 0.02), 0.4), 1.5)
      * (Number.isFinite(gain) && gain > 0 ? gain : 1);
    // The braid keeps more of its own brightness: a hot core is the point.
    this.glowLine = Math.sqrt(this.glow);

    this.scene = new THREE.Scene();
    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    /*
     * Easier on a phone. The scene is mostly additive, so it is fill-rate work
     * rather than geometry, and a handset at three times device pixel ratio is
     * asking for nine times the shading of a laptop for a screen nobody holds
     * that close.
     */
    /*
     * Where it starts. It does not necessarily stay here — see #pace below.
     */
    /*
     * One and a half at most. The scene is glow — ribbons, haze and points of
     * light — and glow at one and a half is indistinguishable from glow at two,
     * where the pixel count is not: a sixteen-inch retina window at two is
     * seven million pixels a frame, at one and a half it is under four. The
     * words are HTML and keep the display's full resolution either way.
     */
    /*
     * Fewer pixels behind a page than under a journey.
     *
     * The journey is the thing being looked at and is worth its resolution. A
     * backdrop is read through: it sits behind body copy, at low contrast, and
     * every pixel it draws is one the GPU is not giving the page. A quarter off
     * the ratio is roughly half the fragments, and at this contrast there is
     * nothing in the frame sharp enough to show the difference.
     */
    this.#dpr = Math.min(devicePixelRatio, this.#back ? 1.1 : 1.5);
    this.renderer.setPixelRatio(this.#dpr);
    this.renderer.domElement.className = 'route-backdrop__canvas';
    this.append(this.renderer.domElement);

    /*
     * The far plane has to clear everything, and at 240 it did not.
     *
     * This is why there was no skyline and no distant mountains: the land runs
     * three hundred and eighty units down the route and three hundred and
     * twenty to each side, and the sky dome sits further out still — all of it
     * beyond the far plane, and so clipped away entirely. The black at the top
     * of the frame was not a dark sky, it was the page showing through where
     * nothing had been drawn. Near goes out to 0.5 at the same time to keep the
     * depth buffer's precision sensible over the longer range.
     */
    this.camera = new THREE.PerspectiveCamera(48, 1, 0.5, 1600);
    // The same lens, in the CSS layer's units. Only its matrices are read.
    this.cssCamera = new THREE.PerspectiveCamera(48, 1, 0.1, 1e6);

    this.#buildRoute();
    this.#buildTable();
    this.#buildSky();
    this.#buildTerrain();
    this.#buildCity();
    this.#buildBranches();
    this.#buildDust();
    if (!this.#back) this.#buildSigns();
    this.#resize();
    /*
     * A phone can take the WebGL context away — running low on memory, or the
     * tab going to the background under pressure. Left alone that leaves a
     * blank canvas over the page with the words hidden behind it. Handing the
     * stops back to the document turns it into the readable list it is without
     * JavaScript.
     */
    this.renderer.domElement.addEventListener('webglcontextlost', (e) => {
      e.preventDefault();
      this.#stop();
      this.closest('.route')?.classList.remove('route--live');
      this.#uncover();
      this.renderer.domElement.remove();
    }, { passive: false });

    addEventListener('resize', () => this.#resize(), { passive: true });
    /*
     * Only where it is going to act on one.
     *
     * A non-passive wheel listener tells the browser every scroll on the page
     * might be cancelled, so it stops handing scrolling to the compositor and
     * runs it on the main thread instead — for the whole document, for the
     * whole visit. "Stop at every sign" needs that; the other two modes read
     * the first line of the handler and return, and were paying full price for
     * it. It is the difference between a page that scrolls and one that feels
     * like it is being dragged.
     */
    /*
     * A backdrop listens to nothing.
     *
     * Everything below is the journey reading the reader: where they are, which
     * way they are going, whether a swipe has been spent. A scene sitting behind
     * a page of text has no stops to bring anyone to, so none of it applies —
     * and a non-passive wheel listener in particular would take scrolling off
     * the compositor for the whole document, on every page, to answer a question
     * nothing asks.
     */
    if (!this.#back) {
    if (this.dataset.snap === 'stop') {
      addEventListener('wheel', this.#onWheel, { passive: false });
    }
    // Where it exists, the browser saying so beats a timer guessing.
    addEventListener('scrollend', this.#onSettled, { passive: true });
    /*
     * Passive, and doing nothing but cancelling. These cost the scroll nothing
     * — it is only a listener that might call preventDefault that takes the
     * page off the compositor.
     */
    addEventListener('scroll', this.#onScrolled, { passive: true });
    if (this.dataset.snap === 'glide') {
      addEventListener('wheel', this.#onWheelStep, { passive: false });
    }
    addEventListener('touchmove', this.#onTouched, { passive: true });
    addEventListener('touchstart', this.#onPush, { passive: true });
    /*
     * One swipe, one glide, on touch as well as on a wheel.
     *
     * Two things were tried here before the pager and both read as two
     * movements: the browser's own snapping, which lets the fling run and then
     * stops it dead at a marker, and reading the swipe on release and easing
     * after it. The pager cancels the scroll instead. See #onPagerMove.
     */
    if (this.dataset.snap === 'glide' && matchMedia('(pointer: coarse)').matches) {
      const page = this.closest('.route') || this;
      page.addEventListener('touchstart', this.#onPagerStart, { passive: true });
      page.addEventListener('touchmove', this.#onPagerMove, { passive: false });
      this.#pager = page;
    }
    addEventListener('keydown', this.#onPush, { passive: true });
    addEventListener('keydown', this.#onTakeover, { passive: true });
    addEventListener('pointerdown', this.#onTakeover, { passive: true });
    }

    // Only now hand the stops over to the component; until this point the
    // section is an ordinary readable list and must stay one.
    this.#route = this.closest('.route');
    this.#route?.classList.add('route--live');
    // Live is one of the ways out, so the cover comes off here as well.
    this.#uncover();
    // The rail needs the section, which is only resolved here.
    if (!this.#back) this.#placeSnaps();

    this.#t0 = performance.now();
    this.#start();
  }

  /*
   * Give the graphics context back.
   *
   * This matters in the theme editor and almost nowhere else. Every change to
   * a setting re-renders the whole section, which discards this element and
   * builds a new one — and a browser allows only a handful of live WebGL
   * contexts, perhaps eight, before it starts refusing new ones or quietly
   * killing the oldest. Left to the garbage collector the old contexts hang
   * around, so after a few edits the scene simply stops drawing and every
   * further change appears to do nothing at all.
   *
   * Stopping the loop is not enough; the context has to be handed back
   * explicitly, and the canvas taken out of the document with it.
   */
  disconnectedCallback() {
    this.#stop();
    removeEventListener('wheel', this.#onWheel);
    removeEventListener('scrollend', this.#onSettled);
    removeEventListener('scroll', this.#onScrolled);
    removeEventListener('wheel', this.#onWheelStep);
    removeEventListener('touchmove', this.#onTouched);
    this.#pager?.removeEventListener('touchstart', this.#onPagerStart);
    this.#pager?.removeEventListener('touchmove', this.#onPagerMove);
    removeEventListener('touchstart', this.#onPush);
    removeEventListener('keydown', this.#onPush);
    clearTimeout(this.#rest);
    removeEventListener('keydown', this.#onTakeover);
    removeEventListener('pointerdown', this.#onTakeover);
    cancelAnimationFrame(this.#slide);

    if (this.renderer) {
      this.renderer.dispose();
      this.renderer.forceContextLoss?.();
      this.renderer.domElement?.remove();
      this.renderer = null;
    }
    /* The stage is appended into the section's own markup rather than this
     * element, so it outlives the element unless it is taken out by hand. */
    this.stage?.remove();
  }

  #buildRoute() {
    const T = this.T;
    const n = this.stops;
    this.group = new T.Group();

    /*
     * Fog is what makes this read as travel. Without it the far end of the road
     * is as sharp as the near end and the eye has nothing to judge distance by;
     * with it, stops resolve out of the dark as you approach and fade behind.
     */
    this.scene.fog = new T.Fog(this.fogColour, FOG_NEAR, FOG_FAR);

    /*
     * A long road running away from the camera. The wander is lateral and
     * vertical but the travel is all in -Z, so scrolling forward is flying
     * forward and every stop arrives rather than slides in.
     */
    const pts = [];
    /*
     * How much road there is, per stop.
     *
     * Seventeen units each was a constant, and it is the thing people mean by
     * a longer journey: not more scrolling over the same drive, which is what
     * Pace does, but further to travel between one sign and the next.
     */
    const reach = Number(this.dataset.reach);
    const per = Number.isFinite(reach) && reach > 0 ? reach : 17;
    const span = per * (n + 1);

    /*
     * Bends measured per unit of road, not per journey.
     *
     * The wander is written against a normalised t, so lengthening the road
     * without touching it would stretch the same two-and-a-bit bends over
     * twice the distance — more road and less to see, which is the opposite of
     * the ask. Scaling the frequencies with the length keeps the turns coming
     * at the same rate per unit travelled.
     *
     * And the control points have to keep up. A Catmull-Rom cannot describe a
     * wave it has no points to bend through: at twice the frequency and the
     * same eleven points the extra turns would simply be averaged away, which
     * is a mistake this curve has made before.
     */
    const bend = per / 17;
    const steps = Math.max(n + 1, Math.round((n + 1) * bend));
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      /*
       * Two frequencies on each axis rather than one. A single sine is a lazy
       * S-bend that the eye reads in advance; layering a second, faster wave at
       * a third of the amplitude gives crests and dips that keep arriving —
       * which is what makes it feel like a journey rather than a curve.
       */
      /*
       * The last stretch is a climb, and then a drop.
       *
       * Arriving on the level, the city was simply there the whole way. Putting
       * a crest in front of it means the reader tops a rise and the basin opens
       * up — which is the difference between scenery and an arrival.
       */
      /*
       * A long, gradual incline rather than a late ramp.
       *
       * Rising fifteen units over a third of the route pitched the camera up
       * hard, which threw the stops over the brightest part of the road just as
       * they were meant to be read. Spread across two thirds of it and lowered,
       * the climb is felt rather than fought.
       */
      const rise = t * t * (3 - 2 * t);
      /*
       * The crest has to sit inside the stretch the reader actually travels.
       *
       * At 0.84 it was past TRAVEL_TO, so the camera never topped it: the road
       * simply rose for the whole journey and the city was in view over a
       * gentle rise from the first stop. Peaking at 0.68 the reader crests with
       * a stretch still to go, and the far side falls thirty-one units into a
       * basin — enough that the ridge blocks the sightline until they are over
       * it, and the city arrives all at once.
       */
      const climb = Math.min(Math.max((t - 0.18) / 0.5, 0), 1);
      const drop = Math.min(Math.max((t - 0.68) / 0.24, 0), 1);
      const crest = climb * climb * (3 - 2 * climb) * 17
        - drop * drop * (3 - 2 * drop) * 31;

      pts.push(new T.Vector3(
        // A slow arc under the existing wander, so the route swings round as
        // well as weaving — one long sweep rather than a corridor.
        Math.sin(t * Math.PI * 2.4 * bend) * 12
          + Math.sin(t * Math.PI * 6.1 * bend) * 3.4
          + Math.sin(t * Math.PI * 0.85 * bend + 0.6) * 11,
        (Math.sin(t * Math.PI * 1.9 * bend) * 7.5
          + Math.cos(t * Math.PI * 5.3 * bend) * 2.6)
          * (1 - rise * 0.55) + crest,
        -t * span
      ));
    }
    this.curve = new T.CatmullRomCurve3(pts, false, 'catmullrom', 0.4);
    this.span = span;
    this.gap = n > 1 ? (LAST_STOP - FIRST_STOP) / (n - 1) : 0;
    /*
     * The real distance between stops, along the road. Taken from the z-extent
     * it was wildly short once the undulation grew — the curve is half as long
     * again as the straight line it spans, and the stops fell out of step with
     * the window that shows them.
     */
    this.stopSpacing = this.gap * this.curve.getLength();

    /*
     * Not one road: a braid of filaments.
     *
     * A single tube is a pipe, and reads as one however much glow is stacked on
     * it. What the reference does is run a bundle of thin strands that drift
     * apart and gather again, so the light has internal structure and the eye
     * has something to follow. Each strand is the same curve with a lateral and
     * vertical offset that varies along its length.
     *
     * They are drawn as camera-facing ribbons rather than tubes.
     *
     * A tube has no radial coordinate to fade across — its uv runs along and
     * around, not outward — so a glow built from concentric tubes is a stack of
     * hard-edged shells, and reads as flat linework however many you add. A
     * ribbon that turns to face the reader has a coordinate straight across its
     * width, which is exactly what a falloff needs: a hairline core and a
     * smooth halo either side, from one pass.
     */
    const up0 = new T.Vector3(0, 1, 0);
    /*
     * Densely sampled. A ribbon is a strip of flat quads, so too few and the
     * curve shows as a chain of straight segments — the jaggedness is the
     * sampling, not the shader.
     */
    const SAMPLES = 760;
    this.strands = [];

    const pos = [], tang = [], side = [], wide = [], lit = [], along = [], seed = [], hot = [];
    const idx = [];
    let base = 0;

    for (let k = 0; k < this.nStrands; k++) {
      const seat = (k / (this.nStrands - 1)) * 2 - 1;
      /*
       * Enough sway to cross a neighbour. Under a third of a unit each strand
       * kept its lane for the whole route and the bundle read as ruled lines;
       * at nearly a whole one they weave through each other, which is what a
       * braid is.
       */
      const sway = 0.28 + (k % 5) * 0.11;
      const freq = 1.1 + (k % 7) * 0.31;
      const drift = 0.42 + (k % 3) * 0.23;
      const climb = 0.85 + (k % 4) * 0.37;
      const phase = k * 1.37;

      const line = [];
      for (let i = 0; i <= 90; i++) {
        const t = i / 90;
        const at = this.curve.getPointAt(t);
        const tan = this.curve.getTangentAt(t);
        const across = new T.Vector3().crossVectors(tan, up0).normalize();
        /*
         * Gentle, and kept inside the width of a road. The bundle plus its
         * wander comes to about three units across: wide enough for the strands
         * to braid, narrow enough that it reads as one carriageway rather than
         * a river delta.
         */
        /*
         * Three waves, not two. The slow one moves a strand from one side of
         * the bundle to the other over the length of the route, so which
         * filaments are crossing changes as you travel rather than repeating.
         */
        const wander = Math.sin(t * Math.PI * freq + phase) * sway
          + Math.sin(t * Math.PI * freq * 2.3 + phase) * sway * 0.34
          + Math.sin(t * Math.PI * drift + phase * 0.6) * sway * 0.45;
        // They rise and fall as well as swing, so the weave is in three
        // dimensions rather than a flat plait seen edge-on.
        const rise = Math.sin(t * Math.PI * climb + phase * 1.7) * 0.35;
        // Every offset from the centre line scales together, so a tighter
        // braid still weaves rather than collapsing into parallel lanes.
        line.push(at.clone()
          .addScaledVector(across, (seat * BRAID_W + wander) * this.spread)
          .add(new T.Vector3(0, 0.04 + (Math.abs(seat) * 0.16 + rise) * this.spread, 0)));
      }

      const strand = new T.CatmullRomCurve3(line, false, 'catmullrom', 0.5);

      /*
       * Every strand its own gauge, its own brightness, and its own heat.
       *
       * An even spread from four-tenths to full was not enough to read. Seventeen
       * ribbons drawn additively pile up, and anywhere two or three overlap the
       * result saturates — so a bundle whose dimmest member is still at forty
       * per cent arrives as one solid mass. Squaring the roll puts most strands
       * low and only a couple near the top, which is the distribution that
       * reads as many separate filaments rather than a rope.
       *
       * Heat is tied to brightness. Only the strongest few carry a core hot
       * enough to blow past the accent; the rest stay the colour they are,
       * which is what stops the whole braid going white.
       */
      /*
       * A ladder, not a lottery.
       *
       * Random brightnesses looked uniform however the distribution was shaped,
       * and the reason is that only the top of it is ever visible: draw
       * seventeen additive ribbons and the half that are dim vanish, leaving
       * whatever happens to be brightest — six values that all landed within a
       * third of each other and so read as one rope.
       *
       * Ranking them instead and stepping the brightness geometrically puts a
       * fixed ratio between any strand and the next, so the ones you can see
       * are guaranteed to differ. Seven is coprime with seventeen, so the ranks
       * scatter across the bundle rather than shading it from one side.
       */
      const rank = (k * this.stride) % this.nStrands;
      /*
       * The ladder guarantees the separation; the jitter stops it looking like
       * a ladder. Without it the steps are a clean geometric series and the eye
       * picks the pattern out.
       */
      const jitter = 0.7 + (((Math.sin(k * 78.233) * 43758.5453) % 1) + 1) % 1 * 0.6;

      /*
       * Shallow across the leading five, then falling away.
       *
       * A single ratio all the way down put only two or three strands near the
       * top and everything else in the tail. Easing the decay over the front of
       * the ladder and steepening it after gives a handful of bright filaments
       * with the rest behind them, which is what a bundle of light looks like.
       */
      /*
       * The ladder, measured across the bundle rather than per step.
       *
       * Stepping a fixed ratio per rank was tuned when there were seventeen
       * strands and quietly stopped working as the count became a setting: at
       * forty, rank thirty is 0.74^24 of the leader — about a thousandth —
       * so three-quarters of the bundle was geometry nobody could see. Adding
       * strands added nothing but vertices, and what was left read as a few
       * bright rules with darkness between them, which is the flatness.
       *
       * Against a normalised rank the shape is the same at twelve strands or
       * sixty: shallow across the leading fifth, steeper after it, and a tail
       * that bottoms out around a fifteenth of the leader rather than at
       * nothing. Still two orders of separation top to bottom, which is what
       * keeps it a bundle of filaments instead of one solid mass.
       */
      const r = this.nStrands > 1 ? rank / (this.nStrands - 1) : 0;
      const soft = Math.pow(0.42, r);
      const cut = Math.pow(0.17, Math.max(0, r - 0.18) / 0.82);
      const bright = soft * cut * jitter;
      // And the leading fifth carry a core hot enough to burn past the accent.
      const heat = Math.max(0, 1 - r / 0.18);
      const halo = 0.18 + bright * 0.5;
      const ph = (k * 0.37) % 1;

      /*
       * Kept so each strand can peel off into the city from where it actually
       * runs, carrying its own gauge and brightness with it — otherwise a
       * branch appears beside it from the centre line, at some other weight.
       */
      this.strands.push({ curve: strand, bright, heat, halo, ph });
      // Distance along the strand in world units, which is what the pulse is
      // measured in — see the fragment shader.
      const strandLen = strand.getLength();

      for (let i = 0; i <= SAMPLES; i++) {
        const u = i / SAMPLES;
        const at = strand.getPointAt(u);
        const tan = strand.getTangentAt(u);

        /*
         * Fade at both ends. A CatmullRom stops dead at its last control point,
         * and seventeen strands stopping together at the same place read as a
         * sunburst pinned to the horizon. Tapered, they simply run out of sight.
         */
        // Fades out where the branch picks it up, rather than at the curve's
        // end — otherwise the two overlap for the whole last quarter.
        const ends = Math.min(u / 0.04, (0.95 - u) / 0.2, 1);
        const taper = ends * ends * (3 - 2 * ends);

        /*
         * And it brightens and dims along its own length. A filament of one
         * even intensity end to end is the thing that reads as drawn rather
         * than lit.
         */
        const flicker = 0.55 + 0.45 * Math.sin(u * Math.PI * (2.3 + (k % 5) * 1.4) + k * 2.1);

        for (const sgn of [-1, 1]) {
          pos.push(at.x, at.y, at.z);
          tang.push(tan.x, tan.y, tan.z);
          side.push(sgn);
          wide.push(halo);
          lit.push(bright * taper * this.glowLine * flicker);
          along.push(u * strandLen);
          seed.push(ph);
          hot.push(heat);
        }
        if (i < SAMPLES) {
          const a = base + i * 2;
          idx.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
        }
      }
      base += (SAMPLES + 1) * 2;
    }

    const braid = new T.BufferGeometry();
    braid.setAttribute('position', new T.Float32BufferAttribute(pos, 3));
    braid.setAttribute('aTangent', new T.Float32BufferAttribute(tang, 3));
    braid.setAttribute('aSide', new T.Float32BufferAttribute(side, 1));
    braid.setAttribute('aWide', new T.Float32BufferAttribute(wide, 1));
    braid.setAttribute('aLit', new T.Float32BufferAttribute(lit, 1));
    braid.setAttribute('aAlong', new T.Float32BufferAttribute(along, 1));
    braid.setAttribute('aSeed', new T.Float32BufferAttribute(seed, 1));
    braid.setAttribute('aHot', new T.Float32BufferAttribute(hot, 1));
    braid.setIndex(idx);

    this.braid = new T.ShaderMaterial({
      transparent: true, depthWrite: false, blending: T.AdditiveBlending,
      side: T.DoubleSide,
      uniforms: {
        uTime: { value: 0 },
        uPx: { value: 0.002 },
        uDpr: { value: 1 },
        uThread: { value: this.thread },
        uPulseSpeed: { value: this.pulseSpeed },
        uViewport: { value: new T.Vector2(1, 1) },
        uCore: { value: this.hot },
        uColor: { value: this.accent },
        uNear: { value: FOG_NEAR },
        uFar: { value: FOG_FAR },
        /*
         * The two halves of how a filament reads, separated so they can be
         * set against each other. `uHalo` is the steady light a strand gives
         * off at any distance; `uSurge` is how much the travelling head lifts
         * it. One of them being much larger than the other is the difference
         * between a lit rope and a dull one with something running through it.
         */
        uHalo: { value: this.haloGain },
        uSurge: { value: this.surgeGain },
      },
      vertexShader: `
        attribute vec3 aTangent;
        attribute float aSide;
        attribute float aWide;
        attribute float aLit;
        attribute float aAlong;
        attribute float aSeed;
        attribute float aHot;
        varying float vSide;
        varying float vLit;
        varying float vDepth;
        varying float vAlong;
        varying float vSeed;
        varying float vDim;
        varying float vHot;
        varying float vWidth;
        uniform float uTime;
        uniform float uPx;
        uniform float uDpr;
        uniform vec2 uViewport;

        void main() {
          vec4 mv = modelViewMatrix * vec4(position, 1.0);
          float depth = max(-mv.z, 0.01);

          /*
           * Widen in screen space.
           *
           * The obvious way is to cross the strand's tangent with the view axis
           * and offset along that. It works right up until a strand points
           * nearly at the reader — then the two vectors are parallel, the cross
           * product collapses toward zero, and normalising it returns whatever
           * the rounding error happened to be. The ribbon flings its vertices
           * in random directions, which is the tangle of shards where the
           * branches turn toward the city.
           *
           * Projecting the line to the screen first and taking the
           * perpendicular there cannot degenerate that way: the offset is a
           * fixed number of pixels, so even a poorly-conditioned direction can
           * only move a vertex by a hairsbreadth rather than across the frame.
           */
          vec4 clip = projectionMatrix * mv;
          vec4 ahead = projectionMatrix * (mv + modelViewMatrix * vec4(aTangent, 0.0));
          vec2 hv = uViewport * 0.5;

          vec2 a = clip.xy / max(clip.w, 0.001) * hv;
          vec2 b = ahead.xy / max(ahead.w, 0.001) * hv;
          vec2 run = b - a;
          vec2 nrm = length(run) > 0.0001
            ? normalize(vec2(-run.y, run.x))
            : vec2(1.0, 0.0);

          /*
           * Never thinner than a pixel either. A ribbon narrower than the pixel
           * it lands on cannot be sampled smoothly — multisampling has the one
           * sample to work with — so it breaks into a dotted stair that crawls
           * as the camera moves. Widened to a pixel and dimmed by exactly the
           * amount it was widened, the light it carries stays the same.
           */
          /*
           * A device pixel, not a CSS one. uViewport is in CSS pixels, so a
           * floor of 1.4 there was 2.8 real pixels on a retina screen — every
           * distant strand drawn twice as wide as it needed to be and dimmed to
           * match, which is soft rather than fine.
           */
          float wantPx = aWide / (depth * uPx);
          float wPx = max(wantPx, 1.4 / uDpr);
          vDim = wantPx / wPx;
          vWidth = wPx * uDpr;

          clip.xy += (nrm * wPx * aSide) / hv * clip.w;

          vDepth = depth;
          vSide = aSide;
          vLit = aLit;
          vHot = aHot;

          // The pulse is worked out per fragment; see there for why.
          vAlong = aAlong;
          vSeed = aSeed;

          gl_Position = clip;
        }`,
      fragmentShader: `
        varying float vSide;
        varying float vLit;
        varying float vDepth;
        varying float vAlong;
        varying float vSeed;
        varying float vDim;
        varying float vHot;
        varying float vWidth;
        uniform float uTime;
        uniform vec3 uCore;
        uniform vec3 uColor;
        uniform float uNear;
        uniform float uFar;
        uniform float uHalo;
        uniform float uSurge;
        uniform float uThread;
        uniform float uPulseSpeed;

        void main() {
          /*
           * The falloff. A hairline core inside a wide, smooth halo — two
           * gaussians across the ribbon's width. This is the thing concentric
           * tubes cannot do: between shells the brightness is flat, so the edge
           * of each one shows and the whole strand reads as a printed rule.
           */
          float s = abs(vSide);
          float halo = exp(-s * s * 6.0);

          /*
           * The core is measured in device pixels, not as a share of the halo.
           *
           * As a share it grew with the halo, and near the camera the halo is
           * hundreds of pixels across — so the "hairline" became a soft bar
           * tens of pixels wide, and the closest strands, the ones the eye goes
           * to first, looked like a low-resolution texture blown up. Capped at a
           * pixel and a bit it stays a filament at any distance. Far away the
           * share is already the narrower of the two, so distant strands keep
           * their proportions; the floor only stops a sub-pixel core, which one
           * shading sample per pixel cannot resolve, crawling as the road moves.
           */
          float px = s * vWidth;
          // Never wider than the ribbon can hold, or the core is cut off at its
          // edge and a thick setting shows a hard rim instead of a line.
          float sigma = min(clamp(vWidth * 0.041, 0.75, 1.1) * uThread, max(vWidth * 0.28, 0.75));
          float core = exp(-px * px / (2.0 * sigma * sigma));

          /*
           * How hard the core burns, and whether it burns past the accent at
           * all, both follow the strand's own heat. A dim filament stays the
           * colour it is; only the strongest go white.
           */
          /*
           * Weighted toward the steady light.
           *
           * The halo is what a filament looks like from any distance; the core
           * is a hairline that only reads close up. At 0.16 the halo was so
           * far under the pulse's 2.6x that the travelling head was the only
           * thing with any presence, which is a surge running through a dull
           * rope rather than a lit one. More halo, and a pulse that lifts
           * rather than dominates.
           */
          float steady = (core * (0.45 + vHot * 0.55) + halo * uHalo) * vLit * vDim;

          /*
           * The pulse is its own light, not a multiple of the steady one.
           *
           * Scaling the strand made the pulse inherit the strand's shape — mostly
           * wide, feathered halo — and its brightness, which the ladder keeps
           * low on most strands on purpose. So the pulse was soft, and invisible
           * on all but the few brightest filaments. Drawn instead on the core and
           * a glow far tighter than the halo, it is a crisp point of light. The
           * square root of the strand's brightness still ranks the pulses, but
           * compresses the ladder enough that a dim strand's pulse can be seen.
           * Between pulses nothing changes: the steady light is untouched.
           */
          /*
           * A comet: a hard leading edge and a tail that decays behind it,
           * measured in world units along the strand.
           *
           * It used to be measured as a share of the strand's whole length —
           * a tail of 4.5% on a road of nearly three hundred units, about
           * twelve. The camera only ever sees a short stretch of road, so one
           * pulse covered most of what was in view: the bundle brightened and
           * dimmed as a whole rather than anything visibly travelling along it,
           * which is why it read as faint and feathered. A pulse this short is
           * narrower than the gap between the ribbon's vertices, so it cannot
           * be interpolated from them; it is computed here from the distance.
           *
           * Several per strand, a fixed spacing apart, so there is always one
           * somewhere in the stretch the camera can see.
           */
          const float SPACING = 60.0;
          float speed = (16.0 + vSeed * 16.0) * uPulseSpeed;
          float f = fract((vAlong - uTime * speed - vSeed * SPACING) / SPACING);
          float lead = 1.0 - smoothstep(0.0, 2.0, f * SPACING);
          float trail = exp(-(1.0 - f) * SPACING / 5.0);

          /*
           * The head is brighter than the tail it drags, so the eye finds the
           * point that is moving rather than a streak that is merely lighter.
           * Core and a glow a few pixels wide only: the halo is
           * hundreds of pixels across near the lens, and a pulse on it there
           * bloomed into a white blob. For the same reason a pulse fades out in
           * the last few units in front of the camera instead of passing
           * through it.
           */
          // A few pixels either side, however wide the ribbon: as a share of a
          // ribbon hundreds of pixels across, the glow's straight leading edge
          // drew a visible block across the road.
          float tight = exp(-px * px / (2.0 * 5.0 * 5.0));
          float near = smoothstep(3.0, 10.0, vDepth);
          float spark = (core * 4.0 + tight * 2.0) * (lead + trail * 0.5)
            * uSurge * sqrt(vLit) * vDim * near;

          if (steady + spark < 0.002) discard;

          float fog = 1.0 - clamp((vDepth - uNear) / (uFar - uNear), 0.0, 1.0);
          vec3 col = mix(uColor, uCore, clamp(core * vHot * 0.95, 0.0, 1.0));

          /*
           * Written as light rather than as opacity, because the pulse has to be
           * able to exceed the strand. The bright filaments already sit at full
           * strength, so a pulse added into a clamped alpha was simply thrown
           * away — the few strands that could carry it most visibly showed it
           * least. The steady light is exactly what it was; the pulse is added
           * on top, burning toward white, and additive blending sums it in.
           */
          vec3 hot = mix(uColor, uCore, 0.85);
          vec3 light = col * clamp(steady, 0.0, 1.0) + hot * spark;
          gl_FragColor = vec4(light * fog, 1.0);
        }`,
    });

    this.group.add(new T.Mesh(braid, this.braid));

    /*
     * No lights. Every surface here is emissive or baked — the strands, the
     * city, the motes and now the land — so a light rig would only be a cost
     * with nothing to shade.
     */

    this.scene.add(this.group);
  }

  /*
   * A lookup of the curve by z, so the land can be shaped around it.
   *
   * Travel is monotonic in -z, so a binary search on the sampled curve gives
   * the road's position at any point on the ground plane in a few steps —
   * which is what lets a thirty-thousand vertex terrain know where the valley
   * is without measuring against the whole curve per vertex.
   */
  #buildTable(n = 800) {
    this.tab = [];
    for (let i = 0; i <= n; i++) this.tab.push(this.curve.getPointAt(i / n));
  }

  #atZ(z) {
    const t = this.tab;
    let lo = 0, hi = t.length - 1;
    if (z >= t[0].z) return t[0];
    if (z <= t[hi].z) return t[hi];
    while (hi - lo > 1) {
      const m = (lo + hi) >> 1;
      if (t[m].z > z) lo = m; else hi = m;
    }
    const a = t[lo], b = t[hi];
    const k = (a.z - z) / (a.z - b.z || 1);
    return { x: a.x + (b.x - a.x) * k, y: a.y + (b.y - a.y) * k };
  }

  /*
   * Open land, not a canyon.
   *
   * The first version put walls close on either side, which made a tunnel: the
   * light had nowhere to go and the scale never landed. This is a single ground
   * mesh running the length of the route and far out to both sides, flattened
   * into a shallow valley where the filaments run so the glow spills across it,
   * rising into hills as it goes away. Layered sines rather than a noise
   * library — four octaves is all this needs and it costs nothing to ship.
   */
  /*
   * Fine detail for the ground away from the carriageway. Separate from the
   * hills because the shapes and the surface want different frequencies: this
   * is the texture you see on a wall, not the wall.
   */
  #rock(x, z) {
    /*
     * Deliberately unrelated directions. Octaves that share a bearing stack
     * into parallel ridges and the whole landscape reads as corduroy; crossing
     * them at odd angles is what makes it rock.
     */
    return Math.sin(x * 0.91 + z * 0.24) * 0.2
      + Math.sin(x * 0.33 - z * 1.42) * 0.16
      + Math.sin(x * 2.17 + z * 0.61) * 0.075
      + Math.sin(x * 0.74 - z * 2.63) * 0.055
      + Math.sin(x * 3.89 + z * 4.31) * 0.022;
  }

  /* Four octaves of layered sine. A noise library for this would be a waste. */
  #hills(x, z) {
    return Math.sin(x * 0.021 - z * 0.048) * 1.5
      + Math.sin(x * 0.055 + z * 0.031) * 1.0
      + Math.sin(x * 0.13 + z * 0.09) * 0.4
      + Math.sin(x * 0.29 - z * 0.23) * 0.2
      + Math.sin(x * 0.71 + z * 0.63) * 0.09
      + Math.sin(x * 1.6 - z * 1.37) * 0.04
      + Math.sin(x * 3.7 + z * 2.3) * 0.008;
  }

  /*
   * Ground height anywhere, and how far that point is from the route. Shared,
   * so the city stands on the same land the terrain draws rather than hovering
   * over its own idea of where the ground is.
   */
  #ground(x, z) {
    const road = this.#atZ(z);
    const lat = Math.abs(x - road.x);

    /*
     * Flat across the carriageway, then climbing. The ramp is short: spread
     * over thirty-odd units the ground barely lifted until it was well past
     * anything the reader looks at, and the verge read as a plain.
     */
    const k = Math.min(Math.max((lat - VALLEY) / 13, 0), 1);
    const ease = k * k * (3 - 2 * k);

    /*
     * Mountains, climbing from the verge — close enough that the braid can
     * throw light on a face, and topping out far enough away to leave sky.
     */
    const f = Math.min(Math.max((lat - 6) / 22, 0), 1);
    const far = f * f * (3 - 2 * f);
    const ridge = far * (Math.abs(this.#hills(x * 0.33, z * 0.33)) * 7 + 3.5);

    /*
     * And a range beyond them, for the skyline.
     *
     * The valley walls were doing both jobs and could not: tall enough to be a
     * horizon they closed over the frame, and low enough to leave sky they had
     * nothing to silhouette. Two scales instead — modest walls beside the road,
     * and real mountains a couple of hundred units out, where height reads as
     * distance rather than as somewhere you could drive.
     */
    const d = Math.min(Math.max((lat - 105) / 130, 0), 1);
    const away = d * d * (3 - 2 * d);
    const range = away * (Math.abs(this.#hills(x * 0.11, z * 0.11)) * 24 + 9);

    /*
     * Texture has its own gate, and it opens at the kerb.
     *
     * Tied to the hills' ramp it was multiplied out precisely where it was
     * wanted: two per cent of it at the verge, full strength only out where
     * nobody is looking. Damped with distance instead, because the grid
     * coarsens outward and detail fine enough to read up close aliases into a
     * comb along the far ridges.
     */
    const g = Math.min(Math.max((lat - 2.6) / 3.5, 0), 1);
    const rough = g * g * (3 - 2 * g);
    const detail = rough * this.#rock(x, z) * (0.75 + ridge * 0.1)
      / (1 + lat / 30);

    /*
     * And it opens out into a basin at the end.
     *
     * The hills and ridges are keyed off lateral distance alone, so they were
     * still rising either side of the city — which put the whole skyline up a
     * hillside rather than on a floor, and left it sitting above the camera
     * after the drop. Flattening the land past the crest gives the arrival
     * somewhere level to stand.
     */
    /*
     * The basin opens and then closes again.
     *
     * Flattening everything past the crest levelled the land behind the city as
     * well as under it, so the arrival had a bare sky above it. The floor runs
     * out at the far side of the skyline and the ground climbs again into a
     * range for the city to stand against.
     */
    const enter = Math.min(Math.max((-z - this.span * 1.0) / (this.span * 0.28), 0), 1);
    const leave = Math.min(Math.max((-z - this.span * 1.56) / (this.span * 0.34), 0), 1);
    const beyond = leave * leave * (3 - 2 * leave);
    const open = enter * enter * (3 - 2 * enter) * (1 - beyond);

    /* The range behind the arrival, keyed off depth rather than width. */
    const backdrop = beyond * (Math.abs(this.#hills(x * 0.09, z * 0.09)) * 30 + 12);

    /*
     * The basin is only as wide as the city needs. Flattening the whole cross
     * section took the mountains with it and left the arrival on a plain with
     * nothing behind it — the floor is for standing the skyline on, not for
     * erasing the horizon.
     */
    const inner = 1 - Math.min(Math.max((lat - 55) / 55, 0), 1);
    const floor = 1 - open * 0.88 * inner;

    return {
      y: road.y - 0.6
        + (ease * (this.#hills(x, z) * 2.2 + 1.4) + ridge + range) * floor
        + backdrop + detail,
      lat, ease, road,
    };
  }

  #buildTerrain() {
    const T = this.T;
    const COLS = 220, ROWS = 400;
    // Far enough past the end that the city has ground under all of it.
    const z0 = 30, z1 = -this.span - 260;

    const pos = [], col = [], idx = [], lamp = [], lampFade = [];
    /*
     * The rock's own colour, tinted toward the accent — but by less the
     * brighter that accent is.
     *
     * The land away from the road takes no light from it, so all it has is this
     * tint, and at a flat three tenths a luminous accent turned every mountain
     * and verge the colour of the braid. Scaling by the same luminance factor
     * the light uses keeps the red exactly as it was and stops yellow painting
     * the whole landscape.
     */
    const rock = this.fogColour.clone().lerp(this.accent, 0.3 * this.glow);

    for (let r = 0; r <= ROWS; r++) {
      const z = z0 + (z1 - z0) * (r / ROWS);
      const road = this.#atZ(z);
      for (let c = 0; c <= COLS; c++) {
        /*
         * Columns are squared about the road rather than spread evenly. Even
         * spacing over two hundred and sixty units puts a vertex every metre
         * and a half everywhere, which is far too coarse to show texture where
         * the reader is actually looking and far finer than needed out at the
         * edge. This packs them against the carriageway for the same count.
         */
        const u = (c / COLS) * 2 - 1;
        const x = road.x + Math.sign(u) * u * u * LAND_W;
        const { y, lat, ease } = this.#ground(x, z);

        pos.push(x, y, z);

        /*
         * The light spilling off the braid is baked into the land rather than
         * lit by lamps. A point light only reaches as far as its own falloff,
         * so the ground glowed in one travelling pool and was black everywhere
         * else — the surroundings barely registered. Distance to the route is
         * already known here, so two exponentials give a hot band along the
         * whole length and a wide wash beyond it, for nothing at run time.
         */
        /*
         * A tighter spill than before. Spread wide it lifted the whole valley
         * to an even wash; held close to the braid it reads as light thrown by
         * the bands, and the ground stays dark enough for them to cut.
         */
        /*
         * Where the light is, relative to this vertex: the braid, which runs
         * along the middle of the corridor a little above the deck. Stored as a
         * direction and a distance so the shading pass can take a proper
         * lambert against it once the normals exist.
         */
        lamp.push(road.x - x, road.y + 0.4 - y, 0);

        /*
         * And it stops where the braid does. The spill is measured from the
         * centre line, which runs to the end of the curve — so past the point
         * the filaments peel off there was a lit strip down the middle of a
         * valley with nothing above it casting it.
         */
        const gone = Math.min(Math.max((-z - this.span * 0.7) / (this.span * 0.18), 0), 1);
        lampFade.push(1 - gone * gone * (3 - 2 * gone));

        /* Fine grain, so the surface has something to catch the light. */
        col.push(Math.sin(x * 2.3 + z * 1.9) * 0.1
          + Math.sin(x * 5.1 - z * 4.3) * 0.06
          + this.#hills(x * 1.7, z * 1.7) * 0.14);
      }
    }

    /*
     * Winding order matters here: rows run in -z and columns in +x, so the
     * obvious order gives every triangle a downward normal and the whole
     * landscape renders unlit and invisible. These two wind anticlockwise seen
     * from above, which is what puts the normals in the sky.
     */
    const w = COLS + 1;
    for (let r = 0; r < ROWS; r++) {
      for (let c = 0; c < COLS; c++) {
        const a = r * w + c;
        idx.push(a, a + 1, a + w, a + 1, a + w + 1, a + w);
      }
    }

    const g = new T.BufferGeometry();
    g.setAttribute('position', new T.Float32BufferAttribute(pos, 3));
    g.setIndex(idx);
    g.computeVertexNormals();

    /*
     * The land is not lit; it is painted.
     *
     * Under real lights an ambient term has to be high enough to show the far
     * hills, and that same term lifts every square unit of ground to the same
     * grey — which is exactly the washed-out look. Baking instead means the
     * only thing brightening the ground is the braid's own spill: away from the
     * road it stays black, and the bands have something to cut against.
     *
     * Relief comes from a lambert term computed once, here, against the normals
     * the geometry already has.
     */
    const normal = g.attributes.normal;
    const shade = new Float32Array(pos.length);
    for (let i = 0, v = 0; i < shade.length; i += 3, v += 1) {
      /*
       * Lit by the braid itself.
       *
       * A fixed key direction lights the flat valley floor as strongly as
       * anything else, which is how the foreground kept flooding while the
       * valley walls stayed pitch black. Taking the lambert against the
       * direction to the road instead means a face turned toward the light
       * catches it and a face turned away does not — so the light is seen
       * falling off against the mountains, which is the whole point of putting
       * them there.
       */
      const lx = lamp[i], ly = lamp[i + 1];
      const d = Math.max(Math.hypot(lx, ly), 0.4);
      const lam = Math.max(
        (normal.getX(v) * lx + normal.getY(v) * ly) / d, 0
      );
      /*
       * A short reach, and almost nothing that is not aimed at the road.
       *
       * Over five and a half units the falloff still had the whole valley floor
       * lit — the camera sits three units off the deck, so ground within ten
       * units of the carriageway fills the frame, and it read as terrain that
       * glows by itself rather than terrain the braid is lighting. The constant
       * term goes with it: that was ambient by another name.
       */
      /*
       * A shorter reach again, and an ambient that is barely there.
       *
       * The far hills had no light on them and still glowed, because the base
       * term is flat across the whole mesh — it lit a mountain two hundred units
       * away exactly as much as the verge. That is what read as fake: a
       * landscape that is its own light source. Cut to a twelfth, it leaves the
       * distance to silhouette against the sky, which is what a night landscape
       * actually does.
       */
      const fall = 1 / (1 + (d / 2.4) ** 2);
      const lit = Math.min((lam * 1.9 + 0.008) * fall * this.glow * lampFade[v], 1.15);
      const base = 0.0055 + col[v] * 0.012;
      shade[i] = Math.min(rock.r * base + this.accent.r * lit, 1);
      shade[i + 1] = Math.min(rock.g * base + this.accent.g * lit, 1);
      shade[i + 2] = Math.min(rock.b * base + this.accent.b * lit, 1);
    }
    g.setAttribute('color', new T.BufferAttribute(shade, 3));

    this.group.add(new T.Mesh(g, new T.MeshBasicMaterial({
      vertexColors: true, fog: true,
    })));
  }


  /*
   * A graded sky with stars, centred on the camera so it is never left behind.
   * Without a horizon the land ends in nothing and the whole scene reads as a
   * tunnel however wide the ground is.
   */
  #buildSky() {
    const T = this.T;
    this.sky = new T.Mesh(
      new T.SphereGeometry(1100, 40, 24),
      new T.ShaderMaterial({
        side: T.BackSide, depthWrite: false, fog: false,
        uniforms: {
          /*
           * The horizon is lifted above the fog colour on purpose. Distant land
           * fades to fog and then silhouettes against this, which is what draws
           * the horizon line — matching the two exactly erases it.
           */
          uTop: { value: this.fogColour.clone().multiplyScalar(2.6) },
          uHorizon: { value: this.fogColour.clone().lerp(this.accent, 0.42 * this.glow) },
          uStar: { value: new T.Color('#FFFFFF').lerp(this.accent, 0.12) },
        },
        vertexShader: `
          varying vec3 vDir;
          void main() {
            vDir = normalize(position);
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }`,
        fragmentShader: `
          varying vec3 vDir;
          uniform vec3 uTop;
          uniform vec3 uHorizon;
          uniform vec3 uStar;

          float hash(vec2 p) {
            return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
          }

          void main() {
            float h = clamp(vDir.y * 1.5, 0.0, 1.0);
            vec3 col = mix(uHorizon, uTop, pow(h, 0.5));

            /*
             * One star per cell, most cells empty. Faded out toward the horizon
             * because that is where the haze and the land are, and a starfield
             * running into the ground looks like dirt on the lens.
             */
            vec2 uv = vec2(atan(vDir.z, vDir.x), asin(clamp(vDir.y, -1.0, 1.0)));
            /*
             * Small and many, rather than few and large. A coarse grid with a
             * wide dot gave each star a fifth of its cell, which at this field
             * of view is a good ten pixels across — planets, not stars.
             */
            vec2 grid = uv * 260.0;
            vec2 cell = floor(grid);
            float pick = hash(cell);
            if (pick > 0.986) {
              float d = length(fract(grid) - 0.5);
              /*
               * Squared, so most are faint and a few are properly bright —
               * which is how a sky looks. An even roll gives a field of
               * identical pinpricks. The brighter ones are drawn a little
               * larger too, since that is what brightness does to a point.
               */
              float roll = hash(cell + 17.3);
              float mag = 0.12 + roll * roll * 2.4;
              col += uStar * smoothstep(0.06 + mag * 0.05, 0.0, d) * mag
                * smoothstep(0.0, 0.22, vDir.y);
            }

            gl_FragColor = vec4(col, 1.0);
          }`,
      })
    );
    this.sky.renderOrder = -1;
    this.scene.add(this.sky);
  }

  #buildCity() {
    const T = this.T;
    /*
     * How many. A setting, because it is the difference between a village and
     * an audience and no number is right for every merchant.
     */
    const N = Math.min(Math.max(Number(this.dataset.crowd) || 900, 60), 2400);
    /*
     * Blocks or pegs.
     *
     * A skyline says "a market"; a field of pegs says "people", which is what
     * the road is actually arriving at. Both are the same instanced mesh with
     * a different unit shape, so it costs one setting and no draw calls.
     */
    const peg = this.dataset.city === 'pegs';
    /*
     * Round, and narrower at the head than the foot.
     *
     * Seven sides at this distance is a heptagon, not a cylinder, and the flat
     * silhouette read as a picket fence. Eighteen makes it round at any size
     * the reader ever sees it, and the taper gives each peg a head and a base —
     * which is the difference between a field of posts and a crowd of people.
     */
    const mesh = new T.InstancedMesh(
      peg
        ? new T.CylinderGeometry(0.3, 0.5, 1, 18)
        : new T.BoxGeometry(1, 1, 1),
      new T.MeshBasicMaterial({
        color: this.accent, transparent: true, opacity: 0.1,
        blending: T.AdditiveBlending, depthWrite: false, fog: true,
      }),
      N
    );

    const m = new T.Matrix4();
    const p = new T.Vector3();
    const q = new T.Quaternion();
    const sc = new T.Vector3();
    const tint = new T.Color();
    /*
     * Well beyond the crest, and a tighter cluster.
     *
     * Set just past the ridge the reader crested straight into it: buildings
     * either side by the fifth stop rather than a skyline in the basin. Fifty
     * units of falling road between the two makes it something arrived at.
     */
    /*
     * How far beyond the crest the crowd stands.
     *
     * A setting, and closer than it was. At 1.38 of the road's own length it
     * sat far enough out that the arrival read as a distant glow rather than
     * as coming among people — the reader crests the last rise and the crowd
     * is still a horizon. Lower numbers bring it up the valley toward the
     * road's end; much below one and it starts to show before the last climb,
     * which is the thing the distance was protecting in the first place.
     */
    const far = Number(this.dataset.cityFar);
    const heart = -this.span * (Number.isFinite(far) && far > 0 ? far : 1.38);
    const centre = this.#atZ(heart);

    this.blocks = [];
    let placed = 0;
    for (let i = 0; i < N * 4 && placed < N; i++) {
      const x = centre.x + (Math.random() - 0.5) * 120;
      const z = heart + (Math.random() - 0.5) * 52;
      // Nothing standing in the carriageway.
      /*
       * Only the carriageway itself is kept clear. A wider exclusion left a
       * corridor straight down the middle of the view, so the city appeared to
       * part around the reader instead of standing at the end of the road.
       */
      const off = Math.abs(x - this.#atZ(z).x);
      if (off < 4.5 || off > 46) continue;

      /*
       * Low. Sixteen units tall it stood from eye level to the top of the frame
       * and read as slabs hanging out of the sky rather than a skyline fifty
       * units off.
       */
      /*
       * Pegs stand at much the same height as each other — a crowd, not a
       * skyline — and are round, so they take one width rather than two.
       */
      const h = peg
        ? 1.1 + Math.pow(Math.random(), 1.6) * 1.9
        : 0.8 + Math.pow(Math.random(), 2.4) * 6;
      const w = peg ? 0.32 + Math.random() * 0.22 : 0.5 + Math.random() * 1.5;
      p.set(x, this.#ground(x, z).y + h / 2, z);
      sc.set(w, h, peg ? w : 0.4 + Math.random() * 1.3);
      m.compose(p, q, sc);
      mesh.setMatrixAt(placed, m);
      mesh.setColorAt(placed, tint.setScalar(0.28 + Math.random() * 0.72));
      // Every third, so the branches have a spread of the crowd to fan across
      // rather than a handful of destinations clustered together.
      if (placed % 3 === 0) this.blocks.push(new T.Vector3(x, this.#ground(x, z).y, z));
      placed += 1;
    }
    mesh.count = placed;
    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    /*
     * Kept, because the crowd is not a fixed thing on the horizon — it comes
     * up as the reader arrives, and the draw loop needs to be able to say so.
     */
    this.city = mesh;
    this.group.add(mesh);
  }

  /*
   * Motes of light through the corridor. Biased toward the road, so the ribbon
   * looks like it is throwing them rather than drifting through a fog bank.
   */
  /*
   * The braid arriving.
   *
   * Past the crest the bundle peels off toward the blocks, a strand at a time,
   * so the road visibly ends in the audience rather than running past it. Same
   * ribbon treatment and the same material as the braid, so they glow alike.
   */
  #buildBranches() {
    const T = this.T;
    if (!this.blocks?.length) return;

    const SAMPLES = 260;
    const up = new T.Vector3(0, 1, 0);
    const pos = [], tang = [], side = [], wide = [], lit = [], along = [], seed = [], hot = [];
    const idx = [];
    let base = 0;

    /*
     * One branch per strand. Twelve of them leaving a twenty-six strand braid
     * left most of the bundle simply running out, so the road looked like it
     * stopped and a few separate lines went on. Every filament now arrives
     * somewhere.
     */
    const targets = this.strands.map((_, i) => this.blocks[i % this.blocks.length]);
    targets.forEach((block, j) => {
      /*
       * Spread over a long stretch of road. Peeling off within a tenth of it
       * they all left from the same place and read as a starburst rather than
       * as the route dividing.
       */
      /*
       * Where a strand hands over to its branch. It has to sit inside the
       * stretch the strand is still lit through, or the two read as separate
       * lines with a gap between them.
       */
      const t0 = 0.7 + (j / Math.max(targets.length - 1, 1)) * 0.16;
      // From the strand itself, so it continues rather than starting afresh.
      const lane = this.strands[j].curve;
      const from = lane.getPointAt(Math.min(t0, 1));
      const tan = lane.getTangentAt(Math.min(t0, 1));
      const across = new T.Vector3().crossVectors(tan, up).normalize();

      /*
       * Leaves along the road, bends toward its block, and arrives running
       * level with the ground. Aimed straight at the target it cut through
       * whatever stood between, and met the block from above like a dropped
       * wire rather than a road reaching it.
       */
      const away = block.clone().sub(from).setY(0).normalize();
      const foot = block.clone().add(new T.Vector3(0, 0.35, 0));
      const branch = new T.CatmullRomCurve3([
        from.clone(),
        from.clone().addScaledVector(tan, 8),
        from.clone().lerp(foot, 0.4).addScaledVector(away, 3).setY(
          from.y + (foot.y - from.y) * 0.35 + 1.2
        ),
        foot.clone().addScaledVector(away, -7).setY(foot.y + 0.5),
        foot,
      ], false, 'catmullrom', 0.4);

      // The same ladder, scattered across the branches.
      // It is the same filament, so it keeps the same gauge and brightness.
      const lit0 = this.strands[j];
      /*
       * A floor under the brightness. The ladder that separates the strands in
       * the bundle is the wrong instrument here: three-quarters of it sits
       * below what reads at all, so three branches arrived at the crowd and
       * the other twenty-three were geometry nobody could see. In the braid
       * that is the point — they pile up. Out here each one is alone against
       * the dark and has to carry itself.
       */
      const bright = Math.max(lit0.bright, 0.2);
      const heat = lit0.heat;
      const halo = lit0.halo * 0.8;
      const ph = lit0.ph;

      /*
       * Ride the ground.
       *
       * The curve on its own is an aerial arc. It leaves the road at the crest
       * and it arrives at the peg, and in between it crosses the basin at
       * whatever height the interpolation happened to choose — which is why
       * the branches read as rays thrown over the valley rather than as the
       * route running down into it. Every sample is set on the terrain
       * instead, at a hover that begins exactly where the strand was already
       * riding and eases down to the peg's foot. It follows the hill down and
       * the verge out, and it ends at the crowd's feet.
       */
      const hover0 = from.y - this.#ground(from.x, from.z).y;
      const path = [];
      for (let i = 0; i <= SAMPLES; i++) {
        const u = i / SAMPLES;
        const at = branch.getPointAt(u);
        const ease = u * u * (3 - 2 * u);
        at.y = this.#ground(at.x, at.z).y + hover0 * (1 - ease) + 0.3 * ease;
        path.push(at);
      }

      let travelled = 0;
      for (let i = 0; i <= SAMPLES; i++) {
        const u = i / SAMPLES;
        const at = path[i];
        if (i > 0) travelled += at.distanceTo(path[i - 1]);
        /*
         * Direction from the ground-hugging line, not from the curve it came
         * from: the ribbon is widened perpendicular to its tangent, and a
         * tangent belonging to a path the light no longer takes twists the
         * ribbon edge-on wherever the terrain has bent it.
         */
        const dir = this.#c
          .copy(path[Math.min(i + 1, SAMPLES)])
          .sub(path[Math.max(i - 1, 0)])
          .normalize();
        /*
         * In from nothing, and dimming as it arrives. Starting at full strength
         * it appeared out of clear air beside the road rather than leaving it.
         */
        const emerge = Math.min(u / 0.16, 1);
        /*
         * Only a little dimmer on arrival. Losing half its light before the
         * end, a branch faded out over the basin and the crowd was lit by
         * nothing — which is the opposite of what the arrival is for.
         */
        const taper = emerge * emerge * (3 - 2 * emerge) * (1 - u * u * 0.22);
        // The same uneven intensity along its length that the braid has.
        const flicker = 0.6 + 0.4 * Math.sin(u * Math.PI * (1.8 + (j % 4)) + j * 2.7);
        for (const sgn of [-1, 1]) {
          pos.push(at.x, at.y, at.z);
          tang.push(dir.x, dir.y, dir.z);
          side.push(sgn);
          wide.push(halo * taper);
          lit.push(bright * taper * this.glowLine * flicker);
          along.push(travelled);
          seed.push(ph);
          hot.push(heat);
        }
        if (i < SAMPLES) {
          const a = base + i * 2;
          idx.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
        }
      }
      base += (SAMPLES + 1) * 2;
    });

    const g = new T.BufferGeometry();
    g.setAttribute('position', new T.Float32BufferAttribute(pos, 3));
    g.setAttribute('aTangent', new T.Float32BufferAttribute(tang, 3));
    g.setAttribute('aSide', new T.Float32BufferAttribute(side, 1));
    g.setAttribute('aWide', new T.Float32BufferAttribute(wide, 1));
    g.setAttribute('aLit', new T.Float32BufferAttribute(lit, 1));
    g.setAttribute('aAlong', new T.Float32BufferAttribute(along, 1));
    g.setAttribute('aSeed', new T.Float32BufferAttribute(seed, 1));
    g.setAttribute('aHot', new T.Float32BufferAttribute(hot, 1));
    g.setIndex(idx);
    this.group.add(new T.Mesh(g, this.braid));
  }

  #buildDust() {
    const T = this.T;
    const N = 4200;
    const pos = [], size = [], alpha = [], tan = [], phase = [];
    const up = new T.Vector3(0, 1, 0);
    const p = new T.Vector3(), tanv = new T.Vector3(), side = new T.Vector3();

    for (let i = 0; i < N; i++) {
      const u = Math.random();
      p.copy(this.curve.getPointAt(u));
      tanv.copy(this.curve.getTangentAt(u));
      side.crossVectors(tanv, up).normalize();

      /*
       * Two populations. Most motes drift through the corridor; a third of them
       * hug the braid itself, which is what puts sparks along the path rather
       * than a haze either side of it.
       */
      const close = i % 2 === 0;
      const r = Math.random() * 2 - 1;
      const lat = close ? r * 1.9 : Math.sign(r) * r * r * 46;
      const hei = close
        ? Math.random() * 2.2 - 0.1
        : Math.pow(Math.random(), 2.2) * 13 - 0.3;

      pos.push(p.x + side.x * lat, p.y + hei, p.z + side.z * lat);

      /*
       * The road's own direction here, so a mote can be carried along it. Over
       * the short run each one travels the road is near enough straight, which
       * is what lets the flow follow the bends without the curve ever reaching
       * the shader.
       */
      tan.push(tanv.x, tanv.y, tanv.z);
      phase.push(Math.random());
      // Squared, so most motes are fine and a few carry real weight.
      size.push((close ? 0.16 : 0.03) + Math.pow(Math.random(), 2.2) * (close ? 0.3 : 0.1));
      alpha.push(((close ? 0.7 : 0.1) + Math.random() * 0.5) * this.glow);
    }

    const g = new T.BufferGeometry();
    g.setAttribute('position', new T.Float32BufferAttribute(pos, 3));
    g.setAttribute('aSize', new T.Float32BufferAttribute(size, 1));
    g.setAttribute('aAlpha', new T.Float32BufferAttribute(alpha, 1));
    g.setAttribute('aTangent', new T.Float32BufferAttribute(tan, 3));
    g.setAttribute('aPhase', new T.Float32BufferAttribute(phase, 1));

    /*
     * A point sprite is a square unless you give it a shape. One 64px radial
     * gradient drawn at build time is cheaper than shipping an image and is the
     * difference between motes of light and confetti.
     */
    const c = document.createElement('canvas');
    c.width = c.height = 64;
    const ctx = c.getContext('2d');
    const grad = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
    grad.addColorStop(0, 'rgba(255,255,255,1)');
    grad.addColorStop(0.35, 'rgba(255,255,255,0.55)');
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 64, 64);
    // Shared: the glow behind the markers on the road wants the same soft disc.
    this.spark = new T.CanvasTexture(c);



    /*
     * PointsMaterial carries one size for every point, which gives a field of
     * identical dots. Size and alpha come per mote instead — the difference
     * between dust and a dot screen.
     */
    this.dust = new T.ShaderMaterial({
      transparent: true, depthWrite: false, blending: T.AdditiveBlending,
      uniforms: {
        uMap: { value: this.spark },
        uColor: { value: this.hot },
        uNear: { value: FOG_NEAR },
        uFar: { value: FOG_FAR },
        uTime: { value: 0 },
        uRun: { value: 11 },
      },
      vertexShader: `
        attribute float aSize;
        attribute float aAlpha;
        attribute vec3 aTangent;
        attribute float aPhase;
        uniform float uTime;
        uniform float uRun;
        varying float vAlpha;
        varying float vDepth;
        void main() {
          /*
           * Each mote runs a short stretch of road and starts again, fading in
           * and out at the ends so nothing pops. Different phases and rates
           * mean the field reads as continuous flow rather than a shoal moving
           * as one.
           */
          float t = fract(aPhase + uTime * (0.05 + aPhase * 0.09));
          vAlpha = aAlpha * smoothstep(0.0, 0.14, t) * smoothstep(1.0, 0.82, t);

          vec3 p = position + aTangent * (t * uRun);
          vec4 mv = modelViewMatrix * vec4(p, 1.0);
          vDepth = -mv.z;
          gl_PointSize = clamp(aSize * 520.0 / max(vDepth, 0.1), 1.0, 15.0);
          gl_Position = projectionMatrix * mv;
        }`,
      fragmentShader: `
        uniform sampler2D uMap;
        uniform vec3 uColor;
        uniform float uNear;
        uniform float uFar;
        varying float vAlpha;
        varying float vDepth;
        void main() {
          float a = texture2D(uMap, gl_PointCoord).a;
          if (a < 0.01) discard;
          float fog = 1.0 - clamp((vDepth - uNear) / (uFar - uNear), 0.0, 1.0);
          gl_FragColor = vec4(uColor, a * vAlpha * fog);
        }`,
    });
    this.group.add(new T.Points(g, this.dust));
  }

  /*
   * One anchor per stop, carrying the HTML sign. The anchor is scaled so the
   * card's pixel dimensions become scene units, which is the contract CSS3D
   * works to: the element is the object.
   */
  /*
   * The earliest point on the road a stop can actually be read.
   *
   * A card is legible over a band of depth — too far and it is a speck, too
   * near and it has already gone by. The reader sets off part of the way onto
   * the road, so a stop placed closer than that reading distance is behind
   * them before it can be seen: it exists, it is positioned correctly, and it
   * never appears once. Asked for three per cent, nobody means "and then hide
   * it", so anything nearer is held at the nearest point that works.
   *
   * The lens decides the distance, which is why this is the same arithmetic
   * the dwell uses rather than a constant.
   */
  #earliest() {
    const lens = (innerHeight / 2) / Math.tan((this.camera.fov * Math.PI) / 360);
    /*
     * Far enough out to play the whole approach, not merely to exist.
     *
     * A first attempt used eight tenths of the lens distance, which put the
     * card exactly on the near edge of the readable band: it appeared at the
     * top of the page at a fraction of full opacity and faded out again within
     * a screen of scrolling, which counts as shown and reads as broken. Past
     * the far edge of the band instead, so the stop comes up out of the
     * distance and arrives like every other one.
     */
    const near = (lens / PX_PER_UNIT) * 1.45;
    return TRAVEL_FROM + near / Math.max(this.curve.getLength(), 1);
  }

  #buildSigns() {
    const T = this.T;
    const up = new T.Vector3(0, 1, 0);

    /*
     * One material for every leader. Soft-edged and floored at a pixel, for the
     * same reason the braid is: a hard white quad a twentieth of a unit wide is
     * sub-pixel at any distance and crawls.
     */


    this.stage = document.createElement('div');
    this.stage.className = 'route__stage';
    const holder = this.parentElement?.querySelector('.route__stops');
    this.holder = holder;
    this.cards = [...(holder?.querySelectorAll('[data-stop]') || [])];
    holder?.append(this.stage);
    this.cards.forEach((c) => this.stage.append(c));

    /*
     * Each card's authored size, kept current without reading layout every
     * frame. A ResizeObserver reports it whenever it actually changes — fonts
     * arriving, an image loading, the viewport narrowing — and the transform
     * the scene applies does not count, so this is the size before perspective.
     */
    const sizes = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const box = entry.borderBoxSize?.[0];
        this.#sizes.set(entry.target, box
          ? [box.inlineSize, box.blockSize]
          : [entry.target.offsetWidth, entry.target.offsetHeight]);
      }
    });
    this.cards.forEach((c) => sizes.observe(c));

    this.panels = [];
    for (let i = 0; i < this.stops; i++) {
      /*
       * Where along the road this stop stands.
       *
       * Evenly spaced unless the stop says otherwise, and the scale is the
       * journey: nought is where the reader sets off and a hundred is the far
       * end, so three per cent is three per cent in. It was measured between
       * the first and last stops to begin with, which is defensible and wrong
       * — asked for three, a person means a little way into the drive, not a
       * little way past a stop that is already a third of the way along. Under
       * that scale three moved a card by a percent and a half of the road,
       * which looks exactly like a setting that does nothing.
       *
       * The far end is the last stop's place rather than the true end of the
       * curve, because the road past it is scenery: travel stops short of it
       * so the final card can be read, and a stop out there would never be
       * arrived at.
       */
      const placed = asNumber(this.cards[i]?.dataset.at);
      const t = Number.isFinite(placed)
        ? Math.max(
          TRAVEL_FROM + Math.min(Math.max(placed / 100, 0), 1) * (LAST_STOP - TRAVEL_FROM),
          this.#earliest(),
        )
        : FIRST_STOP + i * this.gap;
      const at = this.curve.getPointAt(t);
      const ahead = this.curve.getTangentAt(t);
      const side = new T.Vector3().crossVectors(ahead, up).normalize();

      /*
       * Which side of the road the board stands on is decided by where the road
       * goes next, not by alternating. Simply alternating put half of them on
       * the inside of a bend, and the road beyond the stop then swept straight
       * across the board — a bright line running through the middle of the
       * words. Reading the turn and standing the board on the outside of it
       * keeps the road below and clear.
       */
      const onward = this.curve.getTangentAt(Math.min(t + 0.08, 1));
      const turn = new T.Vector3().crossVectors(ahead, onward).y;
      const read = Math.abs(turn) > 1e-4 ? Math.sign(turn) : (i % 2 === 0 ? 1 : -1);

      /*
       * Said, or worked out.
       *
       * The reading above is a good default and it is not an argument. Where a
       * stop names its side, that is the side — the merchant can see the page
       * and this cannot.
       */
      const said = this.cards[i]?.dataset.place;
      const dir = said === 'left' ? -1 : said === 'right' ? 1 : read;

      const anchor = new T.Object3D();
      anchor.userData = {
        t, dir, sign: new T.Object3D(),
        base: at.clone(), side: side.clone().multiplyScalar(dir),
        centre: said === 'centre' || this.cards[i]?.dataset.centre === 'true',
        // What the merchant asked for, before the automatic side was chosen.
        place: said,
        // A stop can drop its leader line: an explicit no, nothing else.
        line: this.cards[i]?.dataset.line !== 'false',
        // Per-stop nudges, in the same units as the section's own: a fraction
        // of the frame from the middle, and up it.
        outX: asNumber(this.cards[i]?.dataset.x) / 100,
        outY: asNumber(this.cards[i]?.dataset.y) / 100,
        // Looked up once. Querying the card for a sprite on every frame of
        // every stop is a DOM search per stop per frame, for an answer that
        // cannot change.
        film: !!this.cards[i]?.querySelector('[data-film]'),
      };
      anchor.position.copy(at).addScaledVector(anchor.userData.side, SIGN_OUT)
        .add(new T.Vector3(0, SIGN_UP, 0));
      this.panels.push(anchor);
      this.group.add(anchor);

      /*
       * The marker on the road, where the leader comes down.
       *
       * White, and a node rather than a pinhead: in the accent at a tenth of a
       * unit it sat on the brightest part of the braid and simply could not be
       * picked out. A small solid centre inside a soft disc reads as a place on
       * the road at any distance.
       */
      const mark = new T.Mesh(
        new T.SphereGeometry(0.075, 14, 14),
        new T.MeshBasicMaterial({
          color: 0xffffff, transparent: true, opacity: 0.95,
          blending: T.AdditiveBlending, depthWrite: false,
        })
      );
      mark.position.copy(at);
      this.group.add(mark);

      const halo = new T.Sprite(new T.SpriteMaterial({
        map: this.spark, color: 0xffffff, transparent: true, opacity: 0,
        blending: T.AdditiveBlending, depthWrite: false, fog: true,
      }));
      halo.scale.setScalar(1.1);
      halo.position.copy(at);
      this.group.add(halo);
      anchor.userData.halo = halo;
      anchor.userData.mark = mark;

      /*
       * The leader from road to sign, as a ribbon rather than a line.
       *
       * A THREE.Line is one device pixel wide whatever you ask for — WebGL
       * ignores linewidth — so against a lit landscape it simply was not there.
       * Four vertices turned to face the reader give it real width, and white
       * keeps it legible over the braid.
       */
      /*
       * No leader. There was one, in three shapes over as many rounds — a
       * diagonal, an elbow, a short pointer — and every one of them read as a
       * line ruled across the picture rather than as part of the scene. The
       * marker standing on the road says which point a stop belongs to, and on
       * a page where only one stop is legible at a time that turns out to be
       * all the relation a reader needs.
       */
      anchor.userData.foot = at.clone();
    }
  }

  /*
   * How deep into the scene a stop sits, if travel were to reach `to`.
   *
   * Depth, not distance along the road. At the end the route drops into the
   * basin, so a stop twelve units further along it can be twenty units deep —
   * which is why setting the end of travel by hand never came out right.
   */
  #stopDepth(panel, to) {
    if (!panel) return 0;

    this.#aim(to);
    this.cssCamera.position.copy(this.camera.position).multiplyScalar(PX_PER_UNIT);
    this.cssCamera.quaternion.copy(this.camera.quaternion);
    this.cssCamera.updateMatrixWorld();
    this.cssCamera.matrixWorldInverse.copy(this.cssCamera.matrixWorld).invert();

    const u = panel.userData;
    this.#v.copy(u.base)
      .addScaledVector(u.side, innerWidth < 700 ? 0 : SIGN_OUT)
      .add(new this.T.Vector3(0, SIGN_UP, 0))
      .multiplyScalar(PX_PER_UNIT)
      .applyMatrix4(this.cssCamera.matrixWorldInverse);
    return -this.#v.z / PX_PER_UNIT;
  }

  /*
   * The furthest stop, which is not necessarily the last one written.
   *
   * Travel has to end where the reader can still see the stop that stands
   * deepest into the scene. With even spacing that is always the final block;
   * once a stop can name its own place on the road, the journey's end belongs
   * to whichever one is furthest along it.
   */
  #furthest() {
    return this.panels.reduce((far, p) => (p.userData.t > far.userData.t ? p : far), this.panels[0]);
  }

  #endDepth(to) {
    return this.#stopDepth(this.#furthest(), to);
  }

  /*
   * Where travel ends: solved, not chosen.
   *
   * The last card has to be at the size it was written at when the reader
   * reaches the foot of the page. That means the camera has to finish close
   * enough, and no constant can know where that is — it depends on the lens,
   * the viewport, how long the curve turned out to be and what the road is
   * doing underneath. Twenty halvings settle it to well under a hundredth.
   */
  #solveTravel() {
    const want = this.capDist;
    const end = this.#furthest().userData.t;
    let lo = Math.max(end - 0.45, 0);
    let hi = Math.max(end - 0.005, 0);
    for (let i = 0; i < 20; i += 1) {
      const mid = (lo + hi) / 2;
      if (this.#endDepth(mid) > want) lo = mid; else hi = mid;
    }
    this.travelTo = (lo + hi) / 2;
  }

  /*
   * The same solve, for every stop: where each one is fully arrived at.
   *
   * A stop reaches the size it was written at when its depth equals the lens
   * distance — the same condition the end of travel is solved for. That is the
   * moment it is meant to be read, so it is also the place scrolling should
   * come to rest, and these are the positions the snap rail is built from.
   */
  #solveStops() {
    /*
     * Two solves per stop, both by the same halving.
     *
     * `stopAt` is where it reaches the size it was written at, which is where
     * the snap markers go. `gone` is where it stops being drawn at all, which
     * a film on the card needs in order to know how long it has: the stop
     * appears on a parametric gate and disappears on a depth one, and nothing
     * relates the two but the shape of the road. Solved once here rather than
     * guessed at per frame.
     */
    const find = (panel, want) => {
      const end = panel.userData.t;
      // Clamped, because a stop placed near the start has less than 0.6 of
      // road behind it and the search would otherwise begin off the end of it.
      let lo = Math.max(end - 0.6, 0);
      let hi = Math.max(end - 0.005, 0);
      for (let i = 0; i < 18; i += 1) {
        const mid = (lo + hi) / 2;
        if (this.#stopDepth(panel, mid) > want) lo = mid; else hi = mid;
      }
      return (lo + hi) / 2;
    };

    this.stopAt = this.panels.map((panel) => find(panel, this.capDist));
    this.panels.forEach((panel) => {
      // Where the dwell runs out, expressed as the same `d` the draw measures.
      panel.userData.gone = panel.userData.t - find(panel, this.capDist * 0.74);
    });
  }

  /*
   * Tell a card whether it is being read.
   *
   * A figure inside a stop — a dial, a bar chart, a number counting up — has
   * to start at the moment the words become legible, which is a fact this
   * loop already holds and nothing outside it does. An intersection observer
   * would be watching a card fixed to the viewport that never intersects
   * anything, and a scroll threshold would be guessing at geometry this has
   * already solved.
   *
   * Called on the ways out as well as the way through. Set only where the
   * card is drawn, the flag survived every path that hides one, so the first
   * stop kept the class for the whole journey and no other figure ever ran.
   */
  #reveal(card, u, on) {
    if (on === u.read) return;
    u.read = on;
    card.classList.toggle('is-shown', on);
    if (on) this.#count(card);
  }

  /*
   * Run any counting numbers in a card up to their value.
   *
   * The finished number is already in the markup — this replaces it for the
   * length of the run and puts it back, so the card reads correctly with no
   * JavaScript and correctly again the instant this is done. Decimal places
   * are taken from how the number was written rather than configured: someone
   * who typed 4.6 means one, and someone who typed 81 means none.
   */
  #count(card) {
    const runs = card.querySelectorAll('[data-count]');
    if (!runs.length) return;
    const still = matchMedia('(prefers-reduced-motion: reduce)').matches;

    runs.forEach((el) => {
      const raw = String(el.dataset.count ?? '').trim();
      const to = Number(raw);
      if (!raw || !Number.isFinite(to)) return;
      const dp = raw.includes('.') ? raw.split('.')[1].length : 0;
      const pre = el.dataset.prefix || '';
      const post = el.dataset.suffix || '';
      const show = (v) => { el.textContent = `${pre}${v.toFixed(dp)}${post}`; };

      cancelAnimationFrame(Number(el.dataset.raf) || 0);
      if (still) { show(to); return; }

      const t0 = performance.now();
      const tick = () => {
        const k = Math.min((performance.now() - t0) / 900, 1);
        show(to * (1 - Math.pow(1 - k, 3)));
        if (k < 1) el.dataset.raf = String(requestAnimationFrame(tick));
        else { delete el.dataset.raf; show(to); }
      };
      show(0);
      el.dataset.raf = String(requestAnimationFrame(tick));
    });
  }

  /*
   * Snap positions, as markers in the document.
   *
   * Left to the browser rather than driven from script: a wheel flick, a
   * trackpad swipe, a touch fling and a keyboard page all arrive through
   * different events with different momentum, and re-implementing that in
   * JavaScript means fighting the scroller and losing to it on one device or
   * another. A one-pixel marker at each solved stop is all the browser needs.
   *
   * `stop` is mandatory snapping with `scroll-snap-stop: always`, which is the
   * only combination that actually holds a fast flick to one sign: with
   * proximity the browser declines to snap at all when the scroll ends far
   * from a stop, and a rule it never applies cannot stop anything.
   *
   * Mandatory means the page can only ever rest at a marker, so the head of
   * the page and the foot of the document get one too — without them the
   * journey would snap away from the intro on load and the footer would be
   * somewhere the reader could not stay.
   */
  #placeSnaps() {
    const route = this.#route;
    if (!route || !this.stopAt) return;

    const mode = this.dataset.snap || 'stop';
    route.classList.toggle('route--snap', mode !== 'off');
    route.classList.toggle('route--snap-stop', mode === 'stop');

    let rail = route.querySelector('.route__snaps');
    if (mode === 'off') { rail?.remove(); return; }
    if (!rail) {
      rail = document.createElement('div');
      rail.className = 'route__snaps';
      rail.setAttribute('aria-hidden', 'true');
      route.append(rail);
    }

    /*
     * The inverse of the travel the loop does: it reads a scroll position and
     * turns it into a point on the curve, and this turns a point on the curve
     * back into the scroll position that would reach it.
     */
    const box = route.getBoundingClientRect();
    const from = box.top + scrollY;
    const span = box.height - innerHeight;
    const reach = (this.travelTo || TRAVEL_TO) - TRAVEL_FROM;
    if (span <= 0 || reach <= 0) { rail.replaceChildren(); return; }

    const marks = [];
    this.stopAt.forEach((at) => {
      const p = (at - TRAVEL_FROM) / reach;
      // Nothing to snap to before the journey starts or after it ends.
      if (p <= 0.02 || p > 1) return;
      const y = from + p * span;
      // Two stops within a screen of each other would read as one stutter.
      if (marks.length && y - marks[marks.length - 1] < innerHeight * 0.6) return;
      marks.push(y);
    });

    if (mode === 'stop') {
      const foot = document.documentElement.scrollHeight - innerHeight;
      marks.unshift(0);
      if (foot > marks[marks.length - 1] + 8) marks.push(foot);
    }

    /*
     * The intro is a stop too, as far as moving around is concerned.
     *
     * It is the first thing in the journey and the only thing with no mark, so
     * a reader coming back up from the first stop had nothing to be taken to:
     * the pager found nothing above, handed the gesture back, and the page
     * scrolled freely — one place on the whole journey behaving unlike the
     * rest. Its position is the top of the section, which is where the intro
     * sits full size.
     */
    if (marks.length && marks[0] - from > innerHeight * 0.4) marks.unshift(from);

    this.marks = marks.map((y) => Math.round(y));
    rail.replaceChildren(...this.marks.map((y) => {
      const mark = document.createElement('div');
      mark.className = 'route__snap';
      // Markers are laid inside the section, so they carry its offset.
      mark.style.top = `${y - Math.round(from)}px`;
      return mark;
    }));
  }

  /*
   * One sign per flick.
   *
   * `scroll-snap-stop: always` is meant to be this on its own, and on touch it
   * is. A trackpad fling is the case it does not cover reliably — the momentum
   * is spent in the compositor before anything gets a say, and the scroll ends
   * three stops down the road at a position that is snapped but wrong. So the
   * wheel is taken over while the reader is on the journey: one gesture is one
   * arrival, and the browser's own smooth scroll carries it there.
   *
   * Only the wheel. Keys and touch already land on the markers through native
   * snapping, and intercepting either costs more than it buys.
   */
  /*
   * The page has stopped moving. If it stopped somewhere other than where the
   * last flick sent it, something else moved it — a key, the scrollbar, a
   * touch — and the remembered target is no longer where the reader is.
   */
  /*
   * Hands off while a hand is on it.
   *
   * scrollend is not the end of the gesture. Between two flicks of a trackpad
   * the browser will report the scroll as finished, and a settle taken at that
   * moment pulls the page back under a reader who has not stopped scrolling —
   * which is the same sticking the CSS snapping was removed for, rebuilt in
   * script. So the end of a scroll only arms a short wait, and any further
   * input disarms it. Nothing moves until the page has genuinely been left
   * alone.
   */
  #onTouched = () => {
    clearTimeout(this.#rest);
  };

  /*
   * One swipe, one stop, on a phone.
   *
   * The wheel version of this takes the gesture over as it happens, because a
   * wheel event can be cancelled. A touch cannot: by the time the finger lifts,
   * the browser has already decided how far it is going to fling, and nothing
   * said here will stop it. So the swipe is read rather than intercepted —
   * where it started, which way it went — and the glide takes over afterwards,
   * holding the stop while the fling spends itself underneath.
   *
   * Measured from where the finger went down, not from where the page has got
   * to by the time it lifts. A flick covers several hundred pixels before the
   * touch even ends, and stepping on from there would skip the stop the reader
   * was asking for.
   */
  /*
   * A phone pages. It does not scroll.
   *
   * Every earlier attempt here let the browser scroll first and corrected it
   * afterwards: read the swipe, work out where it should have gone, ease there.
   * That is always two movements with a pause between them, because the browser
   * has already started moving before the script knows what happened, and no
   * amount of tuning turns two movements into one.
   *
   * So the native scroll is cancelled outright while the journey holds the
   * screen. The page does not follow the finger at all. A drag past the
   * threshold commits to the next stop immediately, mid-gesture rather than on
   * release, and eases there once. There is no position between two stops
   * because the page is never allowed to be in one.
   *
   * Cancelling a touch needs a non-passive listener, which this component
   * avoids everywhere else. It is bound to the section rather than the window,
   * so it exists only for touches that land on the journey, and only where
   * there is no pointer to use the wheel path instead.
   */
  #onPagerStart = (event) => {
    if (event.touches.length > 1) return;
    clearTimeout(this.#rest);
    this.#touchY = event.touches[0].clientY;
    this.#touchFrom = this.#stepTo ?? scrollY;
    this.#pagerDone = false;
    this.#pagerFree = false;
  };

  #onPagerMove = (event) => {
    // A pinch is a zoom, and the second finger is the reader's business.
    if (event.touches.length > 1 || this.#pagerFree) return;
    const marks = this.marks;
    if (!marks || marks.length < 2) return;

    const dy = this.#touchY - event.touches[0].clientY;
    const dir = dy > 0 ? 1 : -1;
    const from = this.#touchFrom;
    const next = dir > 0
      ? marks.find((m) => m > from + 2)
      : [...marks].reverse().find((m) => m < from - 2);

    /*
     * Nothing ahead in the direction being asked for, so the reader is leaving
     * the journey at one end or the other. Hand the gesture back for the rest
     * of this touch and let the page scroll the way any page does.
     */
    if (next == null) { this.#pagerFree = true; return; }

    event.preventDefault();

    if (this.#pagerDone || this.#stepping) return;
    // Enough of a drag to be a direction rather than a tremor.
    if (Math.abs(dy) < 36) return;
    this.#pagerDone = true;
    this.#intent = dir;
    this.#intentAt = performance.now();
    this.#stepTowards(next);
  };

  /*
   * Count the pushes in a scroll, without ever blocking one.
   *
   * A trackpad swipe does not arrive as one event. It is a burst — the finger,
   * then a second or so of momentum dying away — so "one scroll" cannot be
   * counted in events. What marks a new push is the signal climbing back to
   * full strength after it had started to fall, or input starting again after
   * a pause. Momentum only ever decays, so a tail can never be mistaken for a
   * new push, and a reader flicking again mid-glide is caught the moment they
   * do.
   *
   * A wheel held down or spun steadily is a push that never falls away; held
   * at full strength for longer than a flick lasts, it counts as scrolling on.
   *
   * Passive, like every listener here: it reads the deltas and nothing more,
   * so the scroll stays on the compositor where it belongs.
   */
  #begin() {
    return (this.#session ||= { origin: scrollY, pushes: 0, peak: 0, fell: true, at: 0, since: 0, steady: false });
  }

  #onWheelRead = (event) => {
    clearTimeout(this.#rest);
    const now = performance.now();
    const mag = Math.abs(event.deltaY);
    const s = this.#begin();
    if (now - s.at > 140 || (s.fell && mag > 3 && mag >= s.peak * 0.85)) {
      s.pushes += 1;
      s.peak = mag;
      s.fell = false;
      s.since = now;
    } else {
      s.peak = Math.max(mag, s.peak * 0.985);
      if (mag < s.peak * 0.6) s.fell = true;
      /*
       * Held, not swiped. A swipe's finger phase is a few hundred milliseconds
       * before momentum takes over and the signal starts to fall; a wheel held
       * down or a long drag stays up for far longer. Set any shorter and a
       * deliberate slow swipe is taken for scrolling on — which is exactly the
       * one-swipe case this exists to catch.
       */
      if (!s.fell && now - s.since > 750) s.steady = true;
    }
    s.at = now;
  };

  /*
   * One swipe, one stop — held by a lock, not by counting.
   *
   * A single swipe on a Mac trackpad is not one event. It is forty or more over
   * two or three seconds: the finger, then a long tail of momentum. Script
   * cannot see where a gesture begins and ends — the browser knows, the page is
   * not told — so every attempt to count swipes from the events alone
   * eventually reads part of a tail as a fresh swipe. Worse, the previous
   * version only watched: the tail kept moving the page after each glide had
   * begun, and each piece of it was read as another request. That is how a
   * small scroll flew through several stops.
   *
   * So inside the journey the wheel is taken rather than watched. The first
   * event of a gesture moves one stop; everything after it is swallowed until
   * the gesture is spent. What tells a spent gesture from a new one is the
   * shape of the signal: momentum arrives close together, in one direction,
   * and only ever weakens. Input that is steady or strengthening — a new swipe
   * over the top of an old tail, or a mouse wheel being spun — is a request,
   * and once the glide has landed it moves on one more stop. That is how
   * scrolling on keeps going through: one stop per glide, never several at
   * once.
   *
   * Outside the journey — above the first stop, past the last — the wheel is
   * left alone and the page scrolls as any page does.
   */
  #onWheelStep = (event) => {
    if (event.ctrlKey) return; // a pinch is a zoom
    if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
    const marks = this.marks;
    if (!marks || marks.length < 2) return;
    const dir = Math.sign(event.deltaY);
    if (!dir) return;

    /*
     * Which way the reader asked to go, taken from the wheel rather than from
     * the scroll position. The two are not the same thing: a glide that has to
     * pull the page back down onto its target moves the scroll forwards while
     * the reader is going back, and anything reading direction from the scroll
     * alone then believes they turned round. See #onScrolled.
     */
    this.#intent = dir;
    this.#intentAt = performance.now();

    const from = this.#stepTo ?? scrollY;
    const next = dir > 0
      ? marks.find((m) => m > from + 2)
      : [...marks].reverse().find((m) => m < from - 2);
    if (next == null && !this.#stepping && !this.#holding) { this.#deltas = []; return; }
    event.preventDefault();

    const now = performance.now();
    const mag = Math.abs(event.deltaY);
    const fresh = now - this.#wheelAt > 180;
    if (fresh) this.#deltas = [];
    this.#wheelAt = now;
    this.#deltas.push(mag);
    if (this.#deltas.length > 8) this.#deltas.shift();

    if (this.#stepping || now < this.#lockUntil || next == null) return;
    // The dregs of a tail round to a pixel or two and would read as steady.
    if (mag < 4) return;
    if (!fresh && this.#spent()) return;
    this.#stepTowards(next);
  };

  /*
   * Is this the tail of a gesture that has already had its stop? Averages the
   * older half of the recent events against the newer half: a tail weakens,
   * a new push or a spinning wheel holds or rises. Too few events to tell is
   * treated as the same gesture, never as a new one.
   */
  #spent() {
    const d = this.#deltas;
    if (d.length < 4) return true;
    const half = Math.floor(d.length / 2);
    const older = d.slice(0, half).reduce((a, b) => a + b, 0) / half;
    const newer = d.slice(half).reduce((a, b) => a + b, 0) / (d.length - half);
    return newer < older * 0.97;
  }

  #stepTowards(to) {
    this.#stepping = true;
    this.#holding = false;
    this.#stepTo = to;
    const from = scrollY;
    const span = to - from;
    const ms = Math.min(780, Math.max(420, Math.abs(span) * 0.32));
    const t0 = performance.now();
    const frame = (now) => {
      if (this.#stepTo !== to) return; // a new swipe took the glide over
      const k = Math.min((now - t0) / ms, 1);
      scrollTo(0, from + span * (1 - Math.pow(1 - k, 3)));
      if (k < 1) { requestAnimationFrame(frame); return; }
      this.#stepping = false;
      this.#holding = true;
      this.#hold(to, now);
    };
    requestAnimationFrame(frame);
  }

  /*
   * Hold the stop while the rest of the swipe dies away.
   *
   * Safari will not let a wheel event be cancelled once the finger has left the
   * trackpad: the momentum phase is non-cancellable, preventDefault on it does
   * nothing, and the page carries on scrolling underneath whatever the script
   * is doing. During the glide that is invisible, because every frame sets an
   * absolute position and the last word is the script's. The moment the glide
   * ends, though, a second of momentum is still arriving — so the page drifts
   * off the stop it had just reached, and what the reader sees is the journey
   * sliding on by itself after it had settled.
   *
   * Worse, on the way back: the drift carries past the stop, the rest comes to
   * rest outside the band, and the settle that follows reads the page as having
   * come to rest between two stops and carries it on to the next one — forward,
   * away from the stop that was asked for.
   *
   * So the landing is held rather than released. While the wheel is still
   * delivering, the position is re-asserted; once it has been quiet for a
   * moment the hold comes off. There is a ceiling on it so a wheel spun
   * continuously can never be held against — that case wants to run through,
   * and #onWheelStep steps it on again anyway, which takes this over.
   *
   * On any browser that honours preventDefault through the whole gesture this
   * costs nothing: the page has not moved, so every re-assert is a no-op, and
   * the hold ends a tenth of a second after the last event.
   */
  #hold(to, since) {
    const frame = (now) => {
      if (!this.#holding || this.#stepTo !== to) return;
      /*
       * Still being pushed? Two signals, because the two inputs leave different
       * traces. A wheel keeps arriving as events. A finger does not: iOS runs
       * the fling on its own thread and sends no touch events at all while it
       * decays, so the only evidence is the page moving away from where this
       * put it. Either one counts as the gesture still being alive.
       */
      const live = now - this.#wheelAt < 120 || Math.abs(scrollY - to) > 1;
      if (live && now - since < 1600) {
        if (Math.abs(scrollY - to) > 1) scrollTo(0, to);
        requestAnimationFrame(frame);
        return;
      }
      this.#holding = false;
      this.#stepTo = null;
      this.#landed = to;
      this.#landedAt = now;
      this.#lockUntil = now + 160;
    };
    requestAnimationFrame(frame);
  }

  #onPush = () => {
    clearTimeout(this.#rest);
    this.#begin().pushes += 1;
  };

  /*
   * Which way the reader was going, so a glide can never turn them round.
   *
   * Nothing the glide does to the scroll counts. It moves the page to where the
   * reader asked to be, and part of that can be a correction in the opposite
   * direction — pulling back down onto a stop that momentum had carried above.
   * Read as travel, that correction says the reader turned round, and the
   * settle afterwards then sends them on to the stop they were leaving.
   */
  #onScrolled = () => {
    const y = scrollY;
    if (this.#stepping || this.#holding) { this.#lastY = y; return; }
    if (y !== this.#lastY) this.#heading = y > this.#lastY ? 1 : -1;
    this.#lastY = y;
  };

  #onSettled = () => {
    if (this.#aimed !== null && Math.abs(scrollY - this.#aimed) > 60) this.#aimed = null;
    /*
     * The pager owns touch outright. This is the other half of the old
     * behaviour — work out where the reader came to rest and carry them on —
     * and with the pager running it is a second opinion arriving after the
     * first has already landed, which is how one drag ended up three stops
     * along. Where the pager is in charge there is nothing left to settle.
     */
    if (this.#pager) return;
    const mode = this.dataset.snap;
    if ((mode !== 'near' && mode !== 'glide') || !this.marks || this.marks.length < 2) return;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    clearTimeout(this.#rest);
    this.#rest = setTimeout(() => (mode === 'glide' ? this.#carryOn() : this.#settle()), 200);
  };

  /*
   * One scroll, one stop.
   *
   * Measured, a stop is fully readable for about a sixth of the distance to the
   * next, and most of the journey has nothing on screen to read — so free
   * scrolling through it is a hunt. But forcing every scroll to stop at the next
   * sign makes the page a slideshow for someone who wants to move through it.
   *
   * So the reader decides, by how they scroll. A single swipe takes them to the
   * next stop and holds them there — forward if it fell short, back to it if the
   * swipe carried past, because the swipe was only ever asking for one. Scroll on
   * — a second swipe, or a wheel held down — and the journey is theirs to run
   * through at whatever pace they like; when they finally stop, if they have come
   * to rest between two stops, it carries them on to the next one ahead.
   *
   * Nothing moves while a hand is on the page, and a new swipe takes over from a
   * glide the moment it starts.
   */
  #carryOn() {
    const session = this.#session;
    this.#session = null;
    const now = performance.now();
    /*
     * The reader's own direction, where they have given one recently. A wheel
     * says which way they asked to go; the scroll only says which way the page
     * last moved, and after a glide those can disagree.
     */
    const heading = (now - this.#intentAt < 1500 && this.#intent) || this.#heading;
    // ?routedebug: leave a record of how the last scroll was read.
    if (location.search.includes('routedebug')) {
      (window.__route ||= []).push({ y: Math.round(scrollY), heading, ...(session || { none: true }) });
    }
    const marks = this.marks;
    const y = scrollY;
    const gap = Math.abs(marks[1] - marks[0]);
    /*
     * A glide has already answered this. It chose a stop, carried the page
     * there and held it until the swipe was spent, so the rest that follows is
     * its own doing and not a reader who has come to a halt between two stops.
     * Reading it as one is how a swipe back up ended at the stop below.
     */
    if (this.#landed !== null && now - this.#landedAt < 900 && Math.abs(y - this.#landed) < gap * 0.5) return;
    /*
     * A mark is where a stop reaches the size it was written at, which is the
     * far end of the stretch where it is fully readable. Arriving exactly on it
     * leaves the words one small scroll from fading, so a glide lands inside
     * that stretch instead, with room either side.
     */
    /*
     * `band` is the stretch before a stop's mark where it is whole and still
     * growing; `after` is the hold past the mark where it stays at full size.
     * The mark itself is where it arrives at a hundred per cent, and that is
     * where a glide ends — the reader lands on the finished stop, not just
     * short of it.
     */
    const band = gap * 0.16;
    const after = band * 0.48;
    const seat = (m) => m;
    const onStop = marks.some((m) => y >= m - band && y <= m + after);
    const ahead = (from) => (heading > 0
      ? marks.find((m) => seat(m) > from)
      : [...marks].reverse().find((m) => seat(m) < from));

    let target;
    if (session && session.pushes >= 1 && !session.steady) {
      // A nudge that never left the stop it began on is not a request to move.
      if (session.pushes === 1 && onStop && Math.abs(y - session.origin) < after) return;
      /*
       * As many stops as swipes. Two quick swipes were read as "keep going" and
       * ran free, which is how a stop the reader wanted slid past — people
       * swipe twice without meaning anything by it. Counted instead, a reader
       * can only ever pass a stop they asked to pass.
       */
      const list = heading > 0 ? marks : [...marks].reverse();
      const from = session.origin + heading * band * 0.5;
      const beyond = list.filter((m) => (heading > 0 ? seat(m) > from : seat(m) < from));
      target = beyond[Math.min(session.pushes, beyond.length) - 1];
    } else {
      if (onStop) return;
      target = ahead(y + heading * 4);
    }
    if (target == null || Math.abs(seat(target) - y) < 6) return;
    this.#aimed = seat(target);
    scrollTo({ top: this.#aimed, behavior: 'smooth' });
  }

  #settle() {

    /*
     * Settle when close — once the reader has stopped, and never before.
     *
     * This was CSS proximity snapping, which is the browser pulling at the
     * scroll for a few hundred pixels either side of every stop. Those few
     * hundred pixels are exactly where a reader sits, so the whole journey
     * felt as though it were dragging against them: the judder was the page
     * and the reader wanting different things at the same moment.
     *
     * The gesture is left entirely alone now. When it ends, and only if the
     * page came to rest genuinely near a stop rather than somewhere between
     * two, it eases the rest of the way — which is the behaviour the setting
     * was always describing.
     */
    let mark = null;
    let gap = Infinity;
    for (const m of this.marks) {
      const d = Math.abs(m - scrollY);
      if (d < gap) { gap = d; mark = m; }
    }
    if (mark === null) return;
    // A quarter of the way to the next stop: close, by any reading of it.
    const close = Math.abs(this.marks[1] - this.marks[0]) * 0.25;
    if (gap < 6 || gap > close) return;
    this.#aimed = mark;
    scrollTo({ top: mark, behavior: 'smooth' });
  }

  #onWheel = (event) => {
    if (this.dataset.snap !== 'stop' || !this.marks || this.marks.length < 2) return;
    // A pinch is a zoom, not a scroll.
    if (event.ctrlKey) return;
    /*
     * Sideways only. A trackpad puts a little lateral noise on every vertical
     * swipe, and refusing anything with any horizontal component at all meant
     * the first event of a gesture was often waved through uncancelled — after
     * which the browser owns the rest of it and nothing here can take it back.
     */
    if (Math.abs(event.deltaX) > Math.abs(event.deltaY) * 2) return;
    /*
     * Some gestures arrive already committed to the compositor and cannot be
     * cancelled. Stepping anyway would put a scripted scroll and a native one
     * on the page at the same time, pulling against each other.
     */
    if (!event.cancelable) return;

    /*
     * Coasting, or still pushing?
     *
     * This is the whole difficulty, and both earlier attempts got it wrong in
     * opposite directions. A flick is not an event, it is a burst of a hundred
     * of them: the finger lifts and the momentum keeps arriving for another
     * second. Stepping on each of those ran the journey through the middle
     * signs. Then defining a gesture by its silence — one step, nothing more
     * until the events stop — froze the page under a finger that was still
     * moving, because a long scroll never goes quiet.
     *
     * Silence is the wrong signal. Magnitude is the right one. Momentum decays
     * from the moment the finger leaves, so an event well below the largest
     * this gesture has produced is the tail of it and must not move anything.
     * An event still up at that peak is a finger that has not stopped, and is
     * allowed to travel on — at a pace, so that it pages rather than races.
     */
    const now = performance.now();
    const mag = Math.abs(event.deltaY);
    // Silence before this event means a new gesture, which always gets a stop.
    const begun = now - this.#gesture > 120;
    this.#gesture = now;
    if (begun) this.#peak = 0;
    /*
     * The mark to beat, and it fades.
     *
     * Held at the highest value the gesture ever reached, it outlived the
     * gesture: momentum from a hard flick keeps arriving for a second and a
     * half, and a reader who flicks again during it produced events measured
     * against a peak set by the flick before. They lost, so nothing moved, and
     * the page sat under the finger — which is the sticking. Decaying it a
     * little per event forgets the old flick within about a second, while
     * still falling slower than momentum does, so a tail can never overtake
     * it and step on its own.
     */
    this.#peak = Math.max(mag, this.#peak * 0.985);

    // Still at the height of the gesture: a finger, not a tail. Paced, so a
    // long scroll pages through the stops rather than racing them.
    const pushing = mag > this.#peak * 0.85 && now - this.#stepped > 420;
    const fresh = begun || pushing;

    /*
     * Step from where the page is going, not from where it happens to be.
     * Mid-flight the scroll position is somewhere between two stops, and
     * measuring from there would make a second deliberate flick re-target the
     * stop already being travelled to.
     */
    if (this.#aimed !== null && now > this.#until && Math.abs(scrollY - this.#aimed) > 60) {
      this.#aimed = null;
    }
    const base = this.#aimed === null ? scrollY : this.#aimed;

    const next = event.deltaY > 0
      ? this.marks.find((m) => m > base + 4)
      : this.marks.filter((m) => m < base - 4).pop();
    // Past either end there is nothing to step to; let the page scroll.
    if (next === undefined) { this.#aimed = null; return; }

    event.preventDefault();
    if (!fresh) return;

    this.#aimed = next;
    this.#until = now + 1500;
    this.#stepped = now;
    this.#glide(next);
  };

  /*
   * Carry the page to a stop, over a known length of time.
   *
   * `behavior: 'smooth'` was doing this, but its duration is the browser's
   * business and it stretches with distance — a step between two signs took
   * well over a second, and for all of it the reader is pushing at a page that
   * will not answer. Half a second, eased out, and a second flick part way
   * through simply retargets rather than queueing behind it.
   */
  #glide(to) {
    const from = scrollY;
    const span = to - from;
    cancelAnimationFrame(this.#slide);
    if (!span) { this.#aimed = null; return; }
    const t0 = performance.now();
    const ms = Math.min(620, Math.max(300, Math.abs(span) * 0.34));
    const step = () => {
      const k = Math.min((performance.now() - t0) / ms, 1);
      scrollTo(0, Math.round(from + span * (1 - Math.pow(1 - k, 3))));
      if (k < 1) { this.#slide = requestAnimationFrame(step); return; }
      this.#slide = 0;
      this.#aimed = null;
    };
    this.#slide = requestAnimationFrame(step);
  }

  /*
   * Anything that is not the wheel takes the page back. A key, the scrollbar,
   * a link: the glide must let go rather than dragging the reader back to the
   * stop it was headed for.
   */
  #onTakeover = () => {
    // A hand on the page outranks a glide waiting for a swipe to die away.
    if (this.#holding) { this.#holding = false; this.#stepTo = null; }
    if (!this.#slide) return;
    cancelAnimationFrame(this.#slide);
    this.#slide = 0;
    this.#aimed = null;
  };

  /*
   * Carry the road across the frame without turning to face it.
   *
   * Every stop's words start on the page's left margin, and the road runs up
   * the middle — so the one bright thing in the scene passes behind the one
   * thing that has to be read. Moving the road itself would bend the route out
   * of its own geometry, and turning the camera would swing the horizon. This
   * is a shift lens instead, the architectural photographer's trick: the image
   * slides sideways inside the frame and the perspective does not change at
   * all. The vanishing point simply sits further right.
   *
   * Only the scene camera shifts. The stops are placed in screen space against
   * the page grid by their own camera, which copies this one's position and
   * direction but not its lens, so the words stay exactly where they are while
   * the road moves out from behind them.
   *
   * The same lens shifts down as well as across, which is the other half of
   * the trick. On a phone the words run the full width of the screen, so no
   * sideways move alone can get the road out from under them — but the words
   * occupy the upper half and the road only has to be below them. Dropping the
   * image puts the road under the text and more sky behind it, which is the
   * one part of the scene that type can be read against. Raising the camera
   * would do the same to the picture and a different thing to the geometry:
   * the stops are solved against the road's own distances, so moving it would
   * move every one of them. The lens leaves all of that alone.
   */
  #shiftLens() {
    const w = innerWidth;
    const h = innerHeight;
    const px = Math.round(w * this.roadShift);
    const py = Math.round(h * this.roadDrop);
    if (px || py) this.camera.setViewOffset(w, h, -px, -py, w, h);
    else this.camera.clearViewOffset();
  }

  #resize() {
    this.#safe = null;
    this.renderer.setSize(innerWidth, innerHeight, false);
    this.camera.aspect = innerWidth / innerHeight;
    this.camera.updateProjectionMatrix();
    this.#shiftLens();

    // The lens decides where a stop reaches actual size, so the end follows it.
    const lens = this.camera.projectionMatrix.elements[5] * (innerHeight / 2);
    this.capDist = lens / PX_PER_UNIT;
    if (this.panels) {
      this.#solveTravel();
      this.#solveStops();
      this.#placeSnaps();
    }

    // World units per pixel at unit depth — what the ribbons measure against.
    const px = (2 * Math.tan((this.camera.fov * Math.PI) / 360)) / innerHeight;
    if (this.braid) {
      this.braid.uniforms.uPx.value = px;
      this.braid.uniforms.uDpr.value = this.renderer.getPixelRatio();
      this.braid.uniforms.uViewport.value.set(innerWidth, innerHeight);
    }
  }

  /*
   * Hold a steady beat, whatever the screen is.
   *
   * The first version of this measured frames against a fixed twenty-two
   * milliseconds, which is a sixty-hertz assumption. On a ProMotion display the
   * browser asks for a frame every eight: a frame of twelve has already missed
   * its slot, and the old test never counted it — so on exactly the machines
   * with the most pixels to fill, the safety net could not fire.
   *
   * So the beat is learned from the display itself, and the budget is set
   * against it. When frames keep missing, the resolution steps down first; if
   * that is not enough, the scene settles to drawing every other frame. Even
   * pacing at half the rate reads as smooth. Uneven pacing at the full rate —
   * eight, twenty, eight, sixteen — is what reads as judder, and it is worse
   * than a slower rate because the eye sees the stutter, not the speed.
   */
  #pace(now) {
    if (!this.#frame) { this.#frame = now; return; }
    const took = now - this.#frame;
    this.#frame = now;
    if (took > 250) return; // a hidden tab, not a slow frame

    // The display's own interval: the typical gap when nothing is struggling.
    if (this.#beats.length < 40) {
      this.#beats.push(took);
      if (this.#beats.length === 40) {
        const sorted = [...this.#beats].sort((x, y) => x - y);
        this.#beat = sorted[10];
      }
      return;
    }

    const budget = this.#beat * (this.#half ? 2.4 : 1.35);
    this.#slow = took > budget ? this.#slow + 1 : Math.max(0, this.#slow - 1);
    if (this.#slow < 24) return;
    this.#slow = 0;
    if (this.#dpr > 1) {
      this.#dpr = Math.max(1, this.#dpr - 0.25);
      this.renderer.setPixelRatio(this.#dpr);
      this.#resize();
    } else if (!this.#half) {
      this.#half = true;
    }
  }

  #start() {
    const loop = (now) => {
      this.#raf = requestAnimationFrame(loop);
      this.#pace(now || performance.now());
      // At half rate, every other frame is left entirely alone.
      if (this.#half && (this.#skip = !this.#skip)) return;
      /*
       * Progress is measured against the section, not the document.
       *
       * Taken over the whole page the footer counted as travel: the journey
       * finished somewhere up in the middle of it and the last stop had faded
       * out by the time the reader reached the bottom. Measured against the
       * route's own extent, the final card reaches full size exactly as the
       * section's foot meets the bottom of the viewport, and holds there while
       * the footer scrolls up underneath.
       */
      if (this.#back) {
        const t = (performance.now() - this.#t0) / 1000;
        this.braid.uniforms.uTime.value = t;
        this.dust.uniforms.uTime.value = t;
        this.#draw(this.#backdropProgress(t));
        this.renderer.render(this.scene, this.camera);
        return;
      }

      const box = this.#route.getBoundingClientRect();
      const from = box.top + scrollY;
      const span = box.height - innerHeight;

      /*
       * Let go of the cards when the footer takes the screen.
       *
       * The stops are fixed to the viewport, so anything scrolling underneath
       * runs straight into them. On a desktop the footer is a few hundred
       * pixels and sits below the last card perfectly well. On a phone it is
       * taller than the screen — four stacked columns, a form and a row of
       * icons — so it fills the display and the two read on top of each other.
       *
       * Measured as how much viewport is showing below the section rather than
       * as a scroll position, this scales itself: a footer shorter than half a
       * screen never triggers it at all, which is why the desktop end of the
       * journey is untouched, and a tall one releases the card well before the
       * reader is trying to read it. The journey is over by then — travel
       * finishes as the section's foot meets the bottom of the viewport, and
       * all of this happens after that.
       */
      const past = innerHeight - box.bottom;
      const go = Math.min(Math.max((past - innerHeight * 0.5) / (innerHeight * 0.35), 0), 1);
      const fade = 1 - go * go * (3 - 2 * go);
      if (this.holder) {
        this.holder.style.opacity = String(fade);
        this.holder.style.visibility = fade <= 0.01 ? 'hidden' : 'visible';
      }
      /*
       * And the road with them. A transparent footer over a running scene is
       * the point of it on a desktop, where what shows through at the foot of
       * the page is quiet. On a phone the footer arrives over the middle of
       * the crowd, and four columns of links across a field of light is not a
       * footer anybody can read.
       */
      this.style.opacity = String(fade);
      const target = span > 0
        ? Math.min(Math.max((scrollY - from) / span, 0), 1)
        : 0;
      // Ease toward the true position so fast flicks glide rather than snap.
      this.#p += (target - this.#p) * 0.09;
      const t = (performance.now() - this.#t0) / 1000;
      this.braid.uniforms.uTime.value = t;
      this.dust.uniforms.uTime.value = t;
      this.#draw(this.#p);
      this.renderer.render(this.scene, this.camera);
    };
    this.#raf = requestAnimationFrame(loop);
  }

  /*
   * Where the camera is, when nothing is scrolling.
   *
   * The journey's position comes from the reader. A backdrop has no reader to
   * ask, so it comes from the clock instead — and the shape of that matters
   * more than the speed. A loop that ran the full length and started over would
   * cut, every time, from the arrival back to the opening; a cosine turns round
   * at both ends instead, so it never repeats a seam.
   *
   * Held to the open stretch of the road rather than the whole run. The last
   * third of the journey raises a city on the horizon, which is an arrival and
   * reads as one: behind a page about something else it would be a second
   * subject competing with the words. This is the part with nothing in it but
   * the road, which is what a background wants.
   *
   * Four minutes from one end to the other. The braid itself is already moving,
   * on its own clock, so this only has to keep the light from being a fixed
   * shape on the page.
   */
  #backdropProgress(t) {
    /*
     * Parked at the second stop, give or take.
     *
     * Measured on the journey, stop two sits at 0.2124 of the travel: far
     * enough in that the road has shape and the braid is wide, not so far that
     * the horizon has started to carry anything. It is the frame the journey
     * itself spends its best moments in, so it is the one to sit behind a page.
     *
     * The drift around it is two hundredths of the run over ten minutes, which
     * is slow enough that it is never the thing moving. What moves is the braid,
     * on its own clock. This only stops the light being a fixed shape.
     */
    const swing = 0.5 - 0.5 * Math.cos((t / 600) * Math.PI * 2);
    return 0.1924 + 0.04 * swing;
  }

  #stop() { cancelAnimationFrame(this.#raf); this.#raf = 0; }

  /*
   * How long a stop stays readable, as a function of its depth into the scene.
   *
   * Measured in scene units rather than scroll, because the thing that decides
   * it is the lens. Type grows as you approach; at `cap` it is rendering at
   * exactly the size it was written at, and past that it would only get bigger.
   * So it holds through the readable band, reaches full size, and fades from
   * there rather than looming through the frame.
   *
   * The window is exactly one stop's spacing wide, so one finishes as the next
   * begins whether the merchant adds three stops or ten.
   */
  #dwell(depth) {
    const cap = this.capDist;
    const out = cap * 0.74;
    const hold = cap * 1.25;
    /*
     * Arrived, and staying. A stop reaches its written size at `cap`, and it
     * used to begin fading at exactly that point — so "fully arrived" was one
     * position, not a place. A reader carried to it could not rest there: the
     * smallest movement either shrank it or faded it, they nudged, and the
     * nudge was read as the next scroll. It now holds at full size and full
     * strength for a stretch past arrival before it starts to go.
     */
    const keep = cap * 0.88;
    const lead = out + this.stopSpacing;
    if (depth >= lead || depth <= out) return 0;
    if (depth > hold) return (lead - depth) / (lead - hold);
    if (depth > keep) return 1;
    return (depth - out) / (keep - out);
  }

  /*
   * How far a stop is through the window it is actually drawn in.
   *
   * `#dwell` answers how visible a stop is, which rises and falls and so says
   * the same thing twice; a picture on a card has to run one way. The first
   * attempt measured depth between the geometric edges of the dwell, and a
   * third of every film played while the card was still hidden — because what
   * decides whether a stop is drawn at all is not depth but `d`, its place in
   * the order, and the two do not begin together.
   *
   * So this measures the same quantity the gate does, between the same two
   * bounds. The film starts on the frame the card appears and ends on the
   * frame it goes, by construction rather than by coincidence.
   */
  #play(d, u) {
    const from = this.gap * 1.35;
    const to = Number.isFinite(u.gone) ? u.gone : this.gap * -0.1;
    const span = from - to;
    if (span <= 0) return 0;
    return Math.min(Math.max((from - d) / span, 0), 1);
  }

  /*
   * Put the right cell of a sprite sheet in the frame.
   *
   * Background position is a percentage of the *difference* between the image
   * and the box, which is why the divisor is one less than the count: at six
   * columns, the last one sits at 100% and not at 120%.
   */
  #film(card, k) {
    const film = card.querySelector('[data-film]');
    if (!film) return;

    /*
     * Size the box to one cell, once, when Liquid could not.
     *
     * A picked image carries its proportions into the markup; a theme asset
     * has none to carry, and an unsized box collapses to nothing. Reading the
     * sheet's natural size costs one decode the browser was doing anyway.
     */
    if (!film.dataset.sized) {
      film.dataset.sized = 'waiting';
      // Near enough to be worth the download, which is the whole point of
      // naming the sheet rather than setting it in the markup.
      film.dataset.ready = 'true';
      const probe = new Image();
      probe.onload = () => {
        const cols = Number(film.dataset.cols) || 1;
        const rows = Number(film.dataset.rows) || 1;
        film.style.aspectRatio = `${probe.naturalWidth / cols} / ${probe.naturalHeight / rows}`;
        film.dataset.sized = 'true';
      };
      probe.src = film.dataset.src || '';
    }

    const shots = Number(film.dataset.frames) || 1;
    const cols = Number(film.dataset.cols) || 1;
    const rows = Number(film.dataset.rows) || 1;
    const at = Math.min(Math.round(k * (shots - 1)), shots - 1);
    if (film.dataset.at === String(at)) return;
    film.dataset.at = String(at);
    /*
     * Stepped by background position, which repaints only the frame's own
     * few hundred pixels and only when the frame actually changes — thirty-six
     * times a pass. Sliding the whole sheet by transform instead was tried and
     * is worse: it makes the entire sprite sheet a GPU texture, tens of
     * megabytes at retina density, to save a handful of tiny repaints.
     */
    const x = cols > 1 ? ((at % cols) * 100) / (cols - 1) : 0;
    const y = rows > 1 ? (Math.floor(at / cols) * 100) / (rows - 1) : 0;
    film.style.backgroundPosition = `${x}% ${y}%`;
  }

  #draw(p) {
    const T = this.T;
    const at = TRAVEL_FROM
      + Math.min(Math.max(p, 0), 1) * ((this.travelTo || TRAVEL_TO) - TRAVEL_FROM);

    this.#aim(at);
    if (!this.#back) this.#drawStops(p, at);

    /*
     * The crowd comes up as it is unveiled.
     *
     * Held at one brightness it was either a smudge on the horizon for the
     * whole journey or a lit city visible from the first mile. Ramped over the
     * last third — squared, so almost all of it happens on the final climb —
     * it is a glow beyond the ridge that resolves into people as the road
     * drops toward them, which is the arrival the journey is built around.
     */
    if (this.city) {
      const rise = Math.min(Math.max((p - 0.6) / 0.4, 0), 1);
      /*
       * Through the same luminance normalisation as everything else. Nine
       * hundred additive pegs in yellow carry three times the light of the
       * same crowd in blue, and unnormalised the arrival went from a glow to a
       * wall bright enough to read the stop's eyebrow through.
       */
      this.city.material.opacity = (0.07 + 0.5 * rise * rise) * this.glow;
    }
  }

  /*
   * Put the camera where `at` says, and nothing else.
   *
   * Split out so the same placement can be evaluated for a candidate position
   * without drawing — which is what lets the end of the journey be solved for
   * rather than guessed at.
   */
  #aim(at) {
    const T = this.T;
    /*
     * Never off the end of the road, in either direction.
     *
     * getPointAt answers nothing at all for a position outside nought to one,
     * and the next line reads a coordinate off it — so a single out-of-range
     * call throws, the animation loop dies on its first frame, and the whole
     * scene sits there built but motionless. Which is what a stop placed near
     * the start did: the solver searches backwards from it, and from three per
     * cent in, backwards is off the road.
     */
    const eye = this.curve.getPointAt(Math.min(Math.max(at, 0), 1));

    /*
     * Aim at the average of five points ahead rather than one. A single sample
     * sits on the road, so cresting a rise throws the aim skyward and the road
     * reads as a hairpin; averaging across the rise keeps the horizon steady
     * and lets the undulation be felt rather than fought. The deeper the
     * undulation, the further ahead this has to reach.
     */
    const ahead = new T.Vector3();
    [0.025, 0.05, 0.08, 0.115, 0.15].forEach(
      (o) => ahead.add(this.curve.getPointAt(Math.min(at + o, 1)))
    );
    ahead.multiplyScalar(1 / 5);

    /*
     * Rise before the road does.
     *
     * Held at a fixed height over the road directly beneath it, the camera met
     * every rise at its foot: the road ahead climbed past eye level, the view
     * ran up into the braid from below, and the strands splayed out as rays
     * converging on a point in front of the lens. Measured over the journey it
     * went as far as half a unit under the road a little way ahead.
     *
     * So the height is the most it needs to be over the next stretch, with an
     * allowance that grows with distance — the road far ahead may rise a
     * little above where it would sit, near road may not. On the level and on
     * descents this is exactly the old height; approaching a climb it lifts
     * early and smoothly, and never gets within 1.6 units of the road in view.
     */
    let height = eye.y + 3.2;
    for (let o = 0.01; o <= 0.08 + 1e-6; o += 0.01) {
      const q = this.curve.getPointAt(Math.min(at + o, 1));
      height = Math.max(height, q.y + 3.2 - o * 20);
    }
    this.camera.position.set(eye.x, height, eye.z);

    /*
     * At the start the camera aims a little high, which drops the road into the
     * lower third and leaves the top of the frame clear for the opening line.
     * It settles level across the span the intro occupies. The lift is small
     * and the look-ahead long on purpose: an early version lifted 2.6 units over
     * a four-unit look-ahead, which is a thirty-degree pitch on a forty-eight
     * degree lens — the road left the frame altogether.
     */
    const lift = Math.max(0, 1 - (at - TRAVEL_FROM) / 0.15) * 2.4;

    /*
     * The aim height is anchored to the camera, not to the road.
     *
     * Taken from the road ahead alone, the pitch followed every dip: wherever
     * the route descended the view tipped down and there was no sky in frame at
     * all. Anchored to the camera alone, the road fell out of the bottom on the
     * descents instead. Weighted a little toward the anchor keeps the horizon in
     * shot the whole way while the braid stays in view, and the undulation is
     * felt as the road rising and falling through the frame.
     */
    const anchored = this.camera.position.y + 1.0;
    const followed = ahead.y + 1.6;
    this.camera.lookAt(ahead.x, followed * 0.45 + anchored * 0.55 + lift, ahead.z);

    // The sky travels with the reader, or the horizon is left behind.
    this.sky.position.copy(this.camera.position);

    const drift = ahead.x - eye.x;
    this.camera.rotation.z += (-drift * 0.03 - this.camera.rotation.z) * 0.08;
    this.camera.updateMatrixWorld();
    this.camera.matrixWorldInverse.copy(this.camera.matrixWorld).invert();
  }

  /*
   * The part of the screen a stop may occupy.
   *
   * The viewport less a side margin, less whatever sits over the scene at the
   * top and bottom: the header, when it is on screen, and the mobile dock.
   * Measured rather than assumed, because the header may float, stick, or
   * scroll away, and its height changes with the logo and the breakpoint. Read
   * every half second or so rather than every frame — none of it moves quickly
   * — and on resize.
   */
  #safeArea() {
    if (this.#safe && this.#safeAge++ < 30) return this.#safe;
    this.#safeAge = 0;

    const side = innerWidth < 700 ? 16 : Math.round(Math.min(48, Math.max(24, innerWidth * 0.03)));
    let top = 16;
    let bottom = innerHeight - 16;

    const header = document.querySelector('.header');
    if (header) {
      const r = header.getBoundingClientRect();
      if (r.height && r.bottom > 0 && r.top < innerHeight / 2) top = Math.max(top, r.bottom + 12);
    }
    const dock = document.querySelector('.mobile-dock');
    if (dock) {
      const r = dock.getBoundingClientRect();
      if (r.height && r.top < innerHeight && r.bottom > innerHeight / 2) bottom = Math.min(bottom, r.top - 12);
    }

    /*
     * The page's own edges: where the intro, the header and every other
     * section line up. Read from the intro, which is laid out with the same
     * width and margins as the rest of the page.
     */
    let pageLeft = side;
    let pageRight = innerWidth - side;
    const intro = this.parentElement?.querySelector('.route__intro');
    if (intro) {
      const r = intro.getBoundingClientRect();
      if (r.width) {
        pageLeft = Math.max(16, r.left);
        pageRight = Math.min(innerWidth - 16, r.right);
      }
    }

    // The page's margin wins where it is the narrower of the two, so a stop can
    // always reach the edge the intro lines up with.
    this.#safe = {
      left: Math.min(side, pageLeft), right: Math.max(innerWidth - side, pageRight),
      top, bottom, pageLeft, pageRight,
    };
    return this.#safe;
  }

  #drawStops(p, at) {
    const T = this.T;
    this.camera.updateMatrixWorld();
    this.camera.matrixWorldInverse.copy(this.camera.matrixWorld).invert();
    const camUp = this.#up.set(0, 1, 0).applyQuaternion(this.camera.quaternion);

    this.cssCamera.position.copy(this.camera.position).multiplyScalar(PX_PER_UNIT);
    this.cssCamera.quaternion.copy(this.camera.quaternion);
    this.cssCamera.updateMatrixWorld();
    this.cssCamera.matrixWorldInverse.copy(this.cssCamera.matrixWorld).invert();

    const halfW = innerWidth / 2;
    const halfH = innerHeight / 2;
    // halfH / tan(fov/2): the same focal length the canvas projects with.
    const focal = this.camera.projectionMatrix.elements[5] * halfH;
    this.stage.parentElement.style.perspective = `${focal}px`;
    this.stage.style.transform =
      `translateZ(${focal}px)${cameraCSSMatrix(this.cssCamera.matrixWorldInverse)}`
      + `translate(${halfW}px,${halfH}px)`;

    /*
     * Where a stop reaches the size it is written at: the distance whose
     * projection is exactly one element pixel to one screen pixel. It depends
     * on the lens and the viewport, so it is read from the live focal length
     * rather than assumed.
     */
    this.capDist = focal / PX_PER_UNIT;

    this.panels.forEach((anchor, i) => {
      const u = anchor.userData;
      const card = this.cards[i];
      if (!card) return;

      const d = u.t - at;
      /*
       * Over the road on a narrow screen.
       *
       * A phone's field of view is a third the width of a laptop's, so a stop
       * set even one unit to the side subtends most of the frame and lands off
       * the edge. There is no room beside the carriageway on a handset, so the
       * words go above it — which is what the centred setting already does.
       */
      anchor.position.copy(u.base)
        .addScaledVector(u.side, innerWidth < 700 ? 0 : SIGN_OUT)
        .add(new T.Vector3(0, SIGN_UP, 0));

      /*
       * Then placed where it can be read, on every screen.
       *
       * Left at a fixed height above the road it rode the undulation, and left
       * to follow the road sideways it slid across the frame while being read;
       * so the card is held at a set place on the page instead, and the leader
       * down to the marker carries the connection to its place on the road.
       *
       * That set place is only a preference. A card renders at the size it is
       * written at, and a stop with a figure and a few paragraphs is taller than
       * a phone and, off to one side, wider than what is left of a small laptop
       * — so a card centred where it was asked to be ran under the header or
       * off the edge. So the card's full size is measured against the safe area
       * first: scaled down only if it cannot fit at all, and then moved just
       * far enough to bring every edge inside. On a screen with room nothing
       * changes; on one without, it is the nearest placement that fits.
       *
       * Moving along the camera's own up and right vectors does not change a
       * point's depth, so the scale worked out here is the one it renders at.
       */
      this.#v.copy(anchor.position).applyMatrix4(this.camera.matrixWorldInverse);
      const depth = Math.max(-this.#v.z, 0.01);
      const grow = focal / (depth * PX_PER_UNIT);
      const [cardW, cardH] = this.#sizes.get(card) || [card.offsetWidth, card.offsetHeight];
      const safe = this.#safeArea();
      const room = [safe.right - safe.left, safe.bottom - safe.top];
      const fit = cardW && cardH ? Math.min(1, room[0] / cardW, room[1] / cardH) : 1;
      const onScreen = Math.min(grow, 1) * fit;

      /*
       * On a desktop, always to one side, lined up with the page.
       *
       * Held a share of the frame from the middle, a stop started wherever
       * that share happened to land — past the page margin on a wide screen,
       * nearly central on a narrow one — and a centred stop sat in the middle
       * of the road. The intro already lines up with the header and the rest
       * of the page, so the stops do the same: a stop on the left starts where
       * the intro starts, one on the right ends where the page ends. A phone
       * has no room beside the road, so there they stay centred.
       */
      const flat = innerWidth < 700;
      const halfCardW = (cardW * onScreen) / 2;
      const halfCardH = (cardH * onScreen) / 2;
      let cx;
      if (flat) {
        cx = halfW;
      } else if (Number.isFinite(u.outX)) {
        cx = halfW + u.outX * u.dir * halfW;
      } else if (this.alignToPage) {
        /*
         * On the left unless the stop asks for the right.
         *
         * The automatic side follows the road's bend, which is right for a
         * leader line and wrong for reading: the road runs through the middle
         * and right of the frame, so a stop on the right sat over the braid,
         * and a long one stretched back across to the centre. The left is where
         * the intro and the menu start, and where the eye goes back to.
         */
        cx = u.place === 'right' ? safe.pageRight - halfCardW : safe.pageLeft + halfCardW;
      } else {
        cx = halfW + this.signX * u.dir * halfW;
      }
      let cy = halfH - (Number.isFinite(u.outY) ? u.outY : this.signY) * halfH;
      cx = Math.min(Math.max(cx, safe.left + halfCardW), safe.right - halfCardW);
      cy = Math.min(Math.max(cy, safe.top + halfCardH), safe.bottom - halfCardH);

      const wantY = ((halfH - cy) / halfH) * (halfH / focal) * depth;
      const wantX = ((cx - halfW) / halfW) * (halfW / focal) * depth;
      anchor.position.addScaledVector(camUp, wantY - this.#v.y);
      anchor.position.addScaledVector(
        this.#r.set(1, 0, 0).applyQuaternion(this.camera.quaternion),
        wantX - this.#v.x
      );

      const sign = u.sign;
      sign.position.copy(anchor.position).multiplyScalar(PX_PER_UNIT);

      /*
       * Order is parametric; size and fade are not.
       *
       * A stop is only ever shown while it is the one ahead of you. Gating on
       * distance alone put them out of sequence and brought passed ones back:
       * with this much undulation the road snakes hard enough that a stop well
       * behind can be as close, in a straight line, as the one in front.
       */
      if (d < -this.gap * 0.1 || d > this.gap * 1.35) {
        card.style.visibility = 'hidden';
        card.style.pointerEvents = 'none';
        u.halo.material.opacity = 0;
        u.mark.material.opacity = 0;
        this.#reveal(card, u, false);
        return;
      }

      /*
       * Depth along the view axis, not distance to the camera.
       *
       * A CSS perspective scales by depth — that is what `perspective: Npx`
       * means — and a stop set well above the road is a good deal shallower
       * than it is far away. Measured as a straight-line distance it rendered
       * forty per cent over actual size before the cap could ever fire.
       */
      const depthPx = -this.#v.copy(sign.position)
        .applyMatrix4(this.cssCamera.matrixWorldInverse).z;
      const vis = this.#dwell(depthPx / PX_PER_UNIT);
      u.halo.material.opacity = vis * 0.75;
      u.mark.material.opacity = vis * 0.95;

      if (vis <= 0) {
        card.style.visibility = 'hidden';
        card.style.pointerEvents = 'none';
        this.#reveal(card, u, false);
        return;
      }

      /*
       * Freeze at actual size. `grow` is how many screen pixels one element
       * pixel covers; once that passes one the words are as large as they were
       * written, and scaling the object back by the excess pins them there
       * while they fade, instead of letting them swell through the frame.
       */
      const growNow = focal / depthPx;
      /*
       * Growing out of the road is the default. Held at its written size instead
       * — scaled to cancel the perspective — a stop simply fades in place, which
       * is a quieter arrival. It was once thought to be the cheaper one as well;
       * traced, it is not: the re-rendering during an arrival came from the
       * animated figures inside a stop, not from its size changing.
       */
      const hold = this.grow ? (growNow > 1 ? 1 / growNow : 1) : 1 / growNow;
      sign.scale.setScalar(hold * fit);
      sign.quaternion.copy(this.camera.quaternion);
      sign.updateMatrixWorld(true);

      this.#reveal(card, u, vis > 0.55);
      if (u.film) this.#film(card, this.#play(d, u));

      card.style.visibility = 'visible';
      card.style.opacity = String(vis);
      card.style.pointerEvents = vis > 0.6 ? 'auto' : 'none';
      card.style.transform = objectCSSMatrix(sign.matrixWorld);
    });
  }
}

customElements.define('route-backdrop', RouteBackdrop);
